/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// js/helper/currency.js
function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
  function defaultTo(value2, defaultValue) {
    return value2 == null || value2 !== value2 ? defaultValue : value2;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ",");
    decimal = defaultTo(decimal, ".");
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
    return dollarsAmount + centsAmount;
  }
  let value = "";
  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_decimals_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }
  if (formatString.indexOf("with_comma_separator") !== -1) {
    return formatString.replace(placeholderRegex, value);
  } else {
    return formatString.replace(placeholderRegex, value);
  }
}

(function ($) {
  var cartMutation = /\/cart\/(add|change|update|clear)/;

  function disableCheckout() {
    $('.gokwik-checkout button').prop('disabled', true).addClass('disabled');
  }

  var origFetch = window.fetch;
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    if (cartMutation.test(url)) disableCheckout();
    return origFetch.apply(this, arguments);
  };
})(jQuery);

$("#tabfour").click(function(){
  $(".overlayx").addClass("activated");
  $(".auths").addClass("activated");
});

$(".closc, .overlayx ").click(function(){
  $(".overlayx").removeClass("activated");
  $(".auth-sidebar").removeClass("activated");
  $(".popovermfk").removeClass("activated");
});

function themeRefreshCart(openMiniCart) {
  return fetch(window.themeVariables.routes.cartUrl + '.js')
    .then(function (r) { return r.json(); })
    .then(function (cartContent) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cartContent }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cartContent, openMiniCart: !!openMiniCart }
      }));
      return cartContent;
    });
}

/* --- S6 hardening helpers ------------------------------------------------ */
/* Console output is gated: set window.S6_DEBUG = true to see it again. */
function s6log() {
  if (window.S6_DEBUG) {
    try { console.log.apply(console, arguments); } catch (e) {} /* s6-sink */
  }
}
/* Coalesce a high-frequency handler (scroll) onto one animation frame. */
function s6RafThrottle(fn) {
  var queued = false, lastArgs = null, lastThis = null;
  return function () {
    lastArgs = arguments;
    lastThis = this;
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      fn.apply(lastThis, lastArgs);
    });
  };
}
/* Case-insensitive lookup of a discount code inside a list of codes.
   Returns the index in the ORIGINAL list, so the caller keeps the casing
   Shopify stored - never send a lower-cased code back to Shopify. */
function s6IndexOfCode(codes, code) {
  if (typeof code !== 'string') return -1;
  var needle = code.toLowerCase();
  for (var i = 0; i < codes.length; i++) {
    if (typeof codes[i] === 'string' && codes[i].toLowerCase() === needle) return i;
  }
  return -1;
}
function s6SameCode(a, b) {
  return typeof a === 'string' && typeof b === 'string' &&
         a.toLowerCase() === b.toLowerCase();
}
/* ------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------
   On-demand libraries: Swiper, AOS and slick.

   These three used to be downloaded on every page from layout/theme.liquid.
   They are now fetched only when the page actually contains something that
   needs them. Everything below is written so it can never throw - a page
   with no slider must never be able to break the cart.
   ------------------------------------------------------------------------- */

/* Does the page contain anything matching this selector? Never throws. */
function s6HasAny(selector) {
  try { return !!document.querySelector(selector); } catch (e) { return false; }
}

/* One promise per library, so the same <script> is only ever added once. */
var s6LibLoads = {};

/* Is the library on the page already? slick is a jQuery plugin, so it lives
   on jQuery.fn rather than on window. */
function s6LibReady(globalName) {
  try {
    if (globalName === 'slick') {
      return !!(window.jQuery && window.jQuery.fn && window.jQuery.fn.slick);
    }
    return typeof window[globalName] !== 'undefined';
  } catch (e) { return false; }
}

/* Load a library once, then run cb(). Safe to call as often as you like.
     name       - key in window.themeVariables.s6Libs: 'swiper' | 'aos' | 'slick'
     globalName - what the library defines: 'Swiper' | 'AOS' | 'slick'
     cb         - runs only once the library is really available */
function s6LoadLib(name, globalName, cb) {
  try {
    if (s6LibReady(globalName)) { if (cb) cb(); return; }

    var libs = (window.themeVariables && window.themeVariables.s6Libs) || {};
    var url = libs[name];
    if (!url) { s6log('s6LoadLib: no URL for', name); return; }

    if (!s6LibLoads[name]) {
      s6LibLoads[name] = new Promise(function (resolve) {
        var tag = document.createElement('script');
        tag.src = url;
        tag.async = false;        /* keep execution order predictable */
        tag.onload = resolve;
        tag.onerror = resolve;    /* a failed download must not hang the page */
        document.head.appendChild(tag);
      });
    }

    s6LibLoads[name].then(function () {
      try {
        if (s6LibReady(globalName) && cb) cb();
      } catch (e) { s6log('s6LoadLib callback failed', name, e); }
    });
  } catch (e) { s6log('s6LoadLib failed', name, e); }
}

/* Every selector that custom.js turns into a slick slider. If none of them
   are on the page, slick is never downloaded. */
var S6_SLIDER_SELECTORS = [
  '.lwt-row',
  '.testimonial-row',
  '.daily-serving-row',
  '.ingredient-tab-head-wrapper',
  '.videos_list .video_wp',
  '.community-row',
  '.twi3_wrap',
  '.ll3_wrap',
  '.mfktestimonials .slider-nav',
  '.slideshow-secondary-list',
  '.content_image_slider',
  '.protein-ingredients_content-col_items-list',
  '.our-ethos_wrap',
  '.blog-article-row',
  '.one-sachet_items--list',
  '.bundle-page-images--list',
  '[data-slider]',
  '[data-whats-360-cards]',
  '.wellness-routine__track',
  '.enery-listicle-variety-cards',
  '.enery-customer-reviews-grid'
].join(',');

/* Section sliders hand their setup to s6Slider() instead of running it right
   away. The setups are started on the next tick, so several sliders on one
   page share a single slick download. Register ONE slider per call: each
   setup runs in its own try/catch, so a slider that throws (a bad selector
   in one of its event handlers, say) cannot stop the sliders after it. */
var s6SliderSetups = [];
var s6SliderScheduled = false;

function s6Slider(setup) {
  s6SliderSetups.push(setup);
  if (s6SliderScheduled) return;
  s6SliderScheduled = true;
  setTimeout(function () {
    s6SliderScheduled = false;
    s6InitSliders();
  }, 0);
}

/* The one gate for slick: load it only if this page really has a slider,
   then run every setup that was registered - each in its own try/catch so
   one broken slider never blocks the rest. */
function s6InitSliders() {
  if (!s6SliderSetups.length) return;
  if (!s6HasAny(S6_SLIDER_SELECTORS)) { s6SliderSetups.length = 0; return; }

  s6LoadLib('slick', 'slick', function () {
    while (s6SliderSetups.length) {
      var setup = s6SliderSetups.shift();
      try { setup(); } catch (e) { s6log('slider setup failed', e); }
    }
  });
}
/* ------------------------------------------------------------------------- */


$(document).ready(function() {

  $('body').on('click', '.ps-loved', function() {
    var targetId = $(this).data('scroll');
    s6log('targetId',targetId);
    if (!targetId) return;
    var $target = $('#' + targetId);

    if ($target.length) {
        $('html, body').animate({
            scrollTop: $target.offset().top
        }, 500);
    }
  });

  $('.additinal-information-title').click(function(){
    $(this).toggleClass('active');
    $(this).next('.additinal-information-content').toggle();
  })
  /* Section sliders. Each one registers separately, so a slider that
     throws cannot stop the ones after it - see s6InitSliders(). */
  s6Slider(function () {
    $('.lwt-row').slick({
     slidesToShow: 4,
     slidesToScroll: 4,
     arrows: false,
     dots:true,
     responsive: [
        {
          breakpoint: 1199,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 3,
          }
        },
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 2,
          }
        },
        {
          breakpoint: 767,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          }
        }
      ]
    });
  });
  s6Slider(function () {
    $('.testimonial-row').slick({
     slidesToShow: 3,
     slidesToScroll: 3,
     arrows: true,
     dots:false,
     centerMode:true,
     centerPadding:'190px',
     responsive: [
        {
          breakpoint: 1199,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            centerPadding:'140px',
          }
        },
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            centerPadding:'80px'
          }
        },
       {
          breakpoint: 767,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            centerPadding:'30px'
          }
        }
      ]
   });
  });
  s6Slider(function () {
    $('.daily-serving-row').slick({
     slidesToShow: 3,
     slidesToScroll: 3,
     arrows: false,
     dots:true,
     centerMode:true,
     centerPadding:'190px',
     responsive: [
        {
          breakpoint: 1199,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            centerPadding:'140px',
          }
        },
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            centerPadding:'80px'
          }
        },
       {
          breakpoint: 767,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            centerPadding:'30px'
          }
        }
      ]
   });
  });

  s6Slider(function () {
    $('.ingredient-tab-head-wrapper').slick({
     slidesToShow: 4,
     slidesToScroll: 2,
     arrows: true,
     dots:false,
     infinite:false,
     responsive: [
        {
          breakpoint: 1199,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 2
          }
        }
      ]
   });
  });

  s6Slider(function () {
    $('.videos_list .video_wp').slick({
     slidesToShow: 4,
     slidesToScroll: 1,
     arrows: false,
     dots: true,
     infinite:false,
     adaptiveHeight: true,
     autoplay: true,
     autoplaySpeed: 2000,
     prevArrow: $('.prev'),
     nextArrow: $('.next'),
     slide: '.video_main',
     responsive: [
        {
          breakpoint: 1199,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 1
          }
        },
        {
          breakpoint: 767,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1
          }
        },
        {
          breakpoint: 520,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1
          }
        }
      ]
   });
  });

  s6Slider(function () {
    $('.community-row').slick({
       slidesToShow: 4,
       slidesToScroll: 1,
       arrows: true,
       dots:false,
       infinite:false,
       responsive: [
          {
            breakpoint: 1199,
            settings: {
              slidesToShow: 2,
              slidesToScroll: 2
            }
          },
          {
            breakpoint: 767,
            settings: {
              slidesToShow: 1,
              slidesToScroll: 1
            }
          }
        ]
     });
  });

  s6Slider(function () {
     $('.twi3_wrap').slick({
       slidesToShow: 5,
       slidesToScroll: 1,
       arrows: false,
       dots:true,
       infinite:false,
       responsive: [
          {
            breakpoint: 740,
            settings: {
              slidesToShow: 3,
              slidesToScroll: 2,
            }
          }
        ]
      });
  });
  s6Slider(function () {
     $('.ll3_wrap').slick({
       slidesToShow: 5,
       slidesToScroll: 1,
       arrows: false,
       dots:true,
       infinite:false,
       responsive: [
          {
            breakpoint: 740,
            settings: {
              slidesToShow: 4,
              slidesToScroll: 1,
            }
          }
        ]
      });
  });

  s6Slider(function () {
    $('.mfktestimonials .slider-nav').slick({
      slidesToShow: 4,
      slidesToScroll: 1,
      autoplay: true,
       autoplaySpeed: 2000,
      dots: false,
      arrows: false,
      centerMode: true,
      centerPadding:"0px",
      focusOnSelect: true,
      responsive: [
         {
          breakpoint: 768,
          settings: {
            dots: true,
            slidesToShow: 2
          }
        }

      ]
    });
  });

  s6Slider(function () {
    $('.slideshow-secondary-list').slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
       autoplaySpeed: 4000,
      dots: true,
      arrows: false,
    });
  });

  s6Slider(function () {
    $('.content_image_slider').slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: false,
      autoplaySpeed: 2000,
      dots: false,
      arrows: true,
    });
  });

  s6Slider(function () {
     $('.protein-ingredients_content-col_items-list').slick({
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 2000,
      dots: false,
      arrows: true,
    });
  });

  $('body').on('click','.custom_toggle_button', function(){
    var panelId = $(this).attr('aria-controls');
    if (panelId) $('#' + panelId).attr('open','');
    $('.custom_modal__overlay').attr('open','');
    $('body').addClass('lock-all');
  });

  $('body').on('click','.custom_modal__overlay, .custom-modal .modal__close-button', function(){
    $('.custom-modal').removeAttr('open');
    $('.custom_modal__overlay').removeAttr('open');
    $('body').removeClass('lock-all');
  });

  //set cookie
  function setCookie(name, value, days) {
      var d = new Date;
      d.setTime(d.getTime() + 24 * 60 * 60 * 1000 * days);
      document.cookie = name + "=" + value + ";path=/;expires=" + d.toGMTString();
  }

  $('body').on('click','.ctryModal-Content .stay_here_button', function(){
    $(".ctryModal-Content-overlay").removeClass('active');
    $(".ctryModal-Content").removeClass('active');
    $("body").removeClass('overflow-hidden');
    setCookie('redirectchoice',true,30);
  });

  $('.ingredint-head-arrow.left').on('click', function(){
    $('.ingredients-tabs .ingredient-tab-head-wrapper .slick-prev').trigger('click');
  });
  $('.ingredint-head-arrow.right').on('click', function(){
    $('.ingredients-tabs .ingredient-tab-head-wrapper .slick-next').trigger('click');
  });
   // var elem = $(".ingredients-tabs .ingredient-tab-head .ingredient-tab-head-wrapper");
   // var items = elem.children();

   // // Inserting Inner
   // items.wrapAll('<div class="ingredient-tab-link-wrapper" id="inner" />');

   // // Inserting Outer
   // elem.find('#inner').wrap('<div id="outer"/>');

   // var outer = $('#outer');

   // var updateUI = function() {
   //   var maxWidth = outer.outerWidth(true);
   //   var actualWidth = 0;
   //   $.each($('#inner >'), function(i, item) {
   //     actualWidth += $(item).outerWidth(true);
   //   });

   // };
   // updateUI();
   // $('body').on('click','.ingredint-head-arrow.left',function() {
   //   var leftPos = outer.scrollLeft();
   //   outer.animate({
   //     scrollLeft: leftPos - 200
   //   }, 800);
   // });

   // $('body').on('click','.ingredint-head-arrow.right',function() {
   //   var leftPos = outer.scrollLeft();
   //   outer.animate({
   //     scrollLeft: leftPos + 200
   //   }, 800);
   // });

   // $(window).resize(function() {
   //   updateUI();
   // });


  // $('body').on('click','.ingredients-tabs .ingredient-tab-head-wrapper .ingredient-tab-link ', function(e){
  //   e.preventDefault();
  //   $(this).closest('.ingredient-tab-link-wrapper').find('.ingredient-tab-link').removeClass('active');
  //   $(this).addClass('active');
  //   $(this).closest('.ingredients-tab-wrap').find('.ingredients-tab-content-wrapper .ingredients-tab-content').removeClass('active');
  //   $(this).closest('.ingredients-tab-wrap').find('.ingredients-tab-content-wrapper .ingredients-tab-content[data-src="'+ $(this).attr('data-tab') +'"]').addClass('active');
  // });

  $('body').on('click','.ingredients-tabs .ingredient-tab-head-wrapper .ingredient-tab-link ', function(e){
    e.preventDefault();
    $(this).closest('.ingredients-tabs').find('.ingredient-tab-link').removeClass('active');
    $(this).addClass('active');
    $(this).closest('.ingredients-tab-wrap').find('.ingredients-tab-content-wrapper .ingredients-tab-content').removeClass('active');
    $(this).closest('.ingredients-tab-wrap').find('.ingredients-tab-content-wrapper .ingredients-tab-content[data-src="'+ $(this).attr('data-tab') +'"]').addClass('active');
  });

  $('body').on('change','.ingredients-tabs .ingredients-tab-content .vitamin_list', function(){
    $(this).closest('.ingredients-tab-content').find('.ingredients-tab-text').hide();
    $(this).closest('.ingredients-tab-content').find('.ingredients-tab-text[data-content="'+ $(this).val() +'"]').show();
  });

  $('.know-more').click(function(e){
    e.preventDefault();
    $('.indredients-col').removeClass('active')
    $(this).parents('.indredients-col').addClass('active');
  });
  $('.indredients-back-icon').click(function(e){
    e.preventDefault();
    $(this).parents('.indredients-col').removeClass('active');
  });

  // ====
  $('.ingredients-salts-item-image').click(function(e){
    e.preventDefault();
    $('.ingredients-salts-item').removeClass('active')
    $(this).parents('.ingredients-salts-item').addClass('active');
  });
  $('.ingredients-salts-item-inr-back').click(function(e){
    e.preventDefault();
    $(this).parents('.ingredients-salts-item').removeClass('active');
  });

  if (window.matchMedia('(max-width: 740px)').matches){
    $('body').on('click','.footer__item-title', function(){
        $(this).parents('.footer__item--links').toggleClass('active');
        $(this).parents('.footer__item--links').find('.footer__item-content').slideToggle();
    });
  }
  $('.iit-btn').click(function(e){
    e.preventDefault();
    $('.imapge-popup-bg').addClass('active');
  });
  $('.close-popup').click(function(e){
    e.preventDefault();
    $('.imapge-popup-bg').removeClass('active');
  });
  // $(".ingredient-tab-link-wrapper").flickity();
  // $('.ingredient-tab-link-wrapper').flickity({
  //   cellAlign: 'left',
  //   contain: true,
  //   freeScroll: true
  // });

  // $('body').on('click','.videos_list .video_main .slide_video video', function(){
  //    var videle = $('#'+$(this).attr('id')).get(0);
  //    if (videle.paused) {
  //     videle.pause()
  //     videle.muted = true;
  //     $(this).closest('.video_wpp').addClass('paused');
  //   } else {
  //     videle.play()
  //     videle.muted = false;
  //     $(this).closest('.video_wpp').removeClass('paused');
  //   }
  // });

  // $('body').on('click','.videos_list .play-video', function(){
  //   var video = $(this).closest('.video_wpp').find('.slideVideo').attr('id');
  //   var videoele = $('#'+video).get(0);
  //   videoele.play();
  //   videoele.muted = false;
  //   $(this).closest('.video_wpp').removeClass('paused');
  // });

  $('.play-video').on('click', function () {
    const $wrapper = $(this).closest('.video_wpp');
    const $video = $wrapper.find('video')[0];

    // If the clicked video is already playing, pause it
    if ($wrapper.hasClass('playing')) {
      $video.pause();
      $wrapper.removeClass('playing').addClass('paused');
    } else {
      // Pause all other videos
      $('.video_wpp.playing').each(function () {
        const otherVideo = $(this).find('video')[0];
        otherVideo.pause();
        $(this).removeClass('playing').addClass('paused');
      });

      // Play this video
      $video.play();
      $wrapper.addClass('playing').removeClass('paused');
    }
  });


  $(".video_wp").on("afterChange", function (event, slick, currentSlide, nextSlide){
    $('.videos_list').find('.video_main').each(function(){
      var vdid = $(this).find('video').attr('id');
      if (!vdid) return;                 /* no video, or no id on it */
      var videoele = document.getElementById(vdid);
      if (!videoele) return;             /* id points at nothing */
      videoele.pause();
      videoele.muted = false;
      $(this).find('.video_wpp').addClass('paused');
    });
  });

  // $(".video_wp").on("afterChange", function (event, slick, currentSlide, nextSlide) {
  //   var vdid = $(this).find('.video_wp').attr('id');
  //   var videoElement = document.getElementById(vdid);
  //   if (videoElement) {
  //       videoElement.pause();
  //       videoElement.muted = true;
  //       $(this).find('.video_wp').addClass('paused');
  //   } else {
  //       console.error("Video element not found: ", vdid);
  //   }
  // });

  $('body').on('click','.image-with-accordion-item', function(){
    var activeTitle = $(this);
    activeTitle.toggleClass('active');
    var currentDropdown = activeTitle.find('.image-with-accordion-item-text');
    $('.image-with-accordion-item').not(activeTitle).removeClass('active');
    $('.image-with-accordion-item-text').not(currentDropdown).slideUp();
    currentDropdown.stop(true, true).slideToggle();
  });

  if ($(window).width() < 767 && $('.product-form .product-form__buy-buttons').length > 0) {
    var tophe = $('.product-form .product-form__buy-buttons').offset().top + 80;
    $(window).scroll(function(){
      s6log($(window).scrollTop(), tophe);
      if ($(window).scrollTop() >= tophe) {
        $('.product-form .product-form__buy-buttons').addClass('fixed');
        $('.product-form').addClass('fixed');
        // $('.product .product__info').css('padding-bottom',$('.product-form .product-form__buy-buttons').outerHeight()+'px');
      }else{
        $('.product-form .product-form__buy-buttons').removeClass('fixed');
        $('.product-form').removeClass('fixed');
        // $('.product .product__info').css('padding-bottom','0px');
      }
    });
  }
  $('.rwt-title').click(function(e){
    e.preventDefault();
    $('.rwt-title').removeClass('active');
    $(this).addClass('active');
    $('.rwc-item').hide();

    var activeTab = $(this).attr('href');
    $(activeTab).fadeIn();
    return false;
  });

  $('.well-being_tab').click(function(e){
    e.preventDefault();
    $('.well-being_tab').removeClass('active');
    // $('.well-being_tab-dot').removeClass('active');
    $('.well-being_col').hide();

    var activeTab = $(this).attr('href');
    $(`#${activeTab}`).fadeIn();
    $(`.well-being_tab[data-id="${activeTab}"]`).addClass('active');
    // $(`.well-being_tab-dot[data-id="${activeTab}"]`).addClass('active');
    return false;
  });

  $('button.clinical_report-button,.clinical_report-popup-close').click(function(){
    $('.clinical_report-popup-main').toggleClass('active');
  });
  $('button.ps-report-btn, .ps-report-popup-close').click(function(){
    $('.ps-report-popup-main').toggleClass('active');
    $('html').toggleClass('lock-all');
  });
  $('button.real-result-btn, .real-result-popup-close').click(function(){
    $('.real-result-popup-main').toggleClass('active');
    $('html').toggleClass('lock-all');
  });

  $('.exploring-wellness-content-item').first().addClass('active').find('.exploring-wellness-content-item-text').show();
  $('.exploring-wellness-content-item').click(function(){
   $(this).toggleClass('active').siblings().removeClass('active');;
   $(this).find('.exploring-wellness-content-item-text').slideToggle();
    $(this).siblings().find('.exploring-wellness-content-item-text').slideUp();
  });

  const featureItems = document.querySelectorAll(".product-features-item");
    // Function to check if an element is in the viewport
    function isInViewport(element) {
      const rect = element.getBoundingClientRect();
      return rect.top < window.innerHeight - 100 && rect.bottom > 0;
    }
    // Function to handle scroll and show items
    function handleScroll() {
      featureItems.forEach((item) => {
        if (isInViewport(item)) {
          item.classList.add("visible");
        }
      });
    }
    // Run on scroll and on page load
    window.addEventListener("scroll", s6RafThrottle(handleScroll), { passive: true });
    handleScroll(); // Initial check on page load

  // ===============================================
  function isInViewport(el) {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }

  const bottleImage = document.querySelector(".product-features-image-img2");

  if (bottleImage) {
    window.addEventListener("scroll", s6RafThrottle(function () {
      if (isInViewport(bottleImage)) {
        bottleImage.classList.add("visible");
      } else {
        bottleImage.classList.remove("visible");
      }
    }), { passive: true });

    // Initial check on page load
    if (isInViewport(bottleImage)) {
      bottleImage.classList.add("visible");
    }
  }
  // function isInViewport(el) {
  //   if (!el) return false;
  //     const rect = el.getBoundingClientRect();
  //     return rect.top < window.innerHeight - 50 && rect.bottom > 0;
  //   }
  //   function createHandleScroll(imageElement) {  // Closure function
  //     return function handleScroll() {
  //       if (imageElement && isInViewport(imageElement)) {
  //         imageElement.classList.add("visible");
  //       }
  //     };
  //   }
  //   const bottleImage = document.querySelector(".product-features-image-img2");
  //     if (bottleImage) {
  //     const handleScroll = createHandleScroll(bottleImage); // Create the function with bottleImage
  //     window.addEventListener("scroll", handleScroll);
  //     handleScroll();
  //     } else {
  //     console.error("Element with ID 'myBottleImage' not found.");
  //   }

  $('body').on('click','.m-b-i-l-block', function() {
    const modal_item = $(this).data('item');
    $(this).closest('.m-b-i-l-blocks').find('.m-b-i-l-block').removeClass('active');
    $(this).addClass('active');
    s6log('modal_item',modal_item);
    $(this).closest('.mind-body-ingre-inner').find('.m-b-i-modal-item').each(function () {
      const modal_block = $(this).data('modal');
      s6log('modal_block',modal_block);
      if (modal_item == modal_block) {
        $(this).show();
      } else {
        $(this).hide();
      }
    })
  })

});

$('.faq-secondary-toggle').click(function() {
  var $accordionList = $(this).closest('.faq-secondary-list');
  var $accordionItem = $(this).closest('.faq-secondary-item');
  var $accordionContent = $(this).next('.faq-secondary-item-content');
  // Check if the clicked item is already active
  var isActive = $accordionItem.hasClass('active');
  // Remove active class from all items and slide up all contents
  $accordionList.find('.faq-secondary-item').removeClass('active');
  $accordionList.find('.faq-secondary-item-content').slideUp();
  if (!isActive) {
      // Add active class and slide down the current item if it was not active
      $accordionItem.addClass('active');
      $accordionContent.slideToggle();
  }

});



/* Section sliders. Each one registers separately, so a slider that
   throws cannot stop the ones after it - see s6InitSliders(). */
s6Slider(function () {
  $('.our-ethos_wrap').slick({
     slidesToShow: 2,
     slidesToScroll: 1,
     arrows: true,
     dots:true,
     infinite:true,
     responsive: [
      {
        breakpoint: 740,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  });
});
s6Slider(function () {
  $('.blog-article-row').slick({
     slidesToShow: 2,
     slidesToScroll: 1,
     arrows: false,
     dots:true,
     infinite:true,
     responsive: [
      {
        breakpoint: 740,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  });
});

s6Slider(function () {
  $('.one-sachet_items--list').slick({
     slidesToShow: 3,
     slidesToScroll: 1,
     arrows: false,
     dots:true,
     infinite:true,
     responsive: [
      {
        breakpoint: 740,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  });
});

s6Slider(function () {
  $('.bundle-page-images--list').slick({
     slidesToShow: 1,
     slidesToScroll: 1,
     arrows: true,
     dots:false,
     infinite:true,
     responsive: [
      {
        breakpoint: 740,
        settings: {
          arrows: false,
          dots: true
        }
      }
     ]
  });
});

s6Slider(function () {
  $('[data-slider]').slick({
     slidesToShow: 1,
     slidesToScroll: 1,
     arrows: true,
     dots:false,
     infinite:true,
     responsive: [
      {
        breakpoint: 740,
        settings: {
          arrows: false,
          dots: true
        }
      }
     ]
  });
});

document.addEventListener("variant:changed", function (event) {
  let variant = event.detail.variant;
  let form = event.target;
  var product_handle = $("body").attr("product-handle");
  $.get(`/products/${product_handle}?variant=${variant.id}`, function (data) {
    $(".product-meta__serving-text_wrapper")?.html(
      $(data).find(".product-meta__serving-text_wrapper").html()
    );
  });
});

$(document).on('change', 'input[name="properties[flavours]"]', function() {
  // Get the selected value
  const selectedValue = $(this).val();

  // Update the element text
  $('.usp-icons-title_value').text(selectedValue);
});


// function existingDiscounts(){
//     const discountCodes = [];
//     const discountPills = $('.cart-cart-coupon_offer-popover').find('.applied_coupon-item');
//     for (const pill of discountPills) {
//       if (typeof $(pill).attr('data-discount-code') === 'string') {
//         discountCodes.push($(pill).attr('data-discount-code'));
//       }
//     }
//     return discountCodes;
//   }

//   function applied_code(discountCodeValue, _this, sectionId){
//     console.log('0000',discountCodeValue, _this, sectionId)
//     var existingDiscountsValue = existingDiscounts();
//     if (existingDiscountsValue.includes(discountCodeValue)) return;
//     console.log('111111111111111')

//     $(_this).attr("disabled","disabled");
//     $(_this).attr("loading","loading");

//     async function getupdatedata(){
//       let response = await fetch(`${window.themeVariables.routes.cart_update_url}.js`, {
//         method: "POST", // HTTP method (GET, POST, PUT, DELETE, etc.)
//         headers : {
//           "Content-Type": "application/json"
//         },
//         body: JSON.stringify({
//           discount: [...existingDiscountsValue, discountCodeValue].join(','),
//           sections: [sectionId]
//         })
//       });
//       const data = await response.json();
//       return data;
//     }
//     getupdatedata().then(data => {
//       console.log(data);
//       if (data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
//           return discount.code === discountCodeValue && discount.applicable === false;
//         })
//       ) {
//         $(_this).removeAttr('disabled');
//         $(_this).removeAttr('loading');
//         $(_this).removeClass('loading');
//         $('.cart-offer-coupon_applied-message').append(`<div id="applied_code_error" class="applied_code_error text--xsmall">This discount code cannot be applied to your cart</div>`);
//         setTimeout(function(){
//           $('.applied_code_error').remove();
//         },2000);
//         return;
//       }

//       // const newHtml = data.sections[sectionId];
//       // const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
//       // $('.mini-cart__drawer-footer').html($(parsedHtml).find('.mini-cart__drawer-footer').html());
//       // $('#coupon-offers').html($(parsedHtml).find('#coupon-offers').html());
//       // $(_this).removeClass('loading');
//       $.getJSON("/cart.js", function (cart) {
//         document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
//           bubbles: true,
//           detail: { cart }
//         }));
//       });
//     });
//   }


//   $('body').on('keyup','.cart-offer-coupon_input-field', function(){
//     if($(this).val() != ''){
//      $(this).closest('.cart-offer-coupon_input').find('.cart-offer-coupon_apply-button').addClass('active');
//     }else{
//      $(this).closest('.cart-offer-coupon_input').find('.cart-offer-coupon_apply-button').removeClass('active');
//     }
//   });

//   $('body').on('click','.cart-offer-coupon_apply-button.active', function(){
//     var _this  = this;
//     var discountCodeValue = $(this).closest('.cart-offer-coupon_input').find('.cart-offer-coupon_input-field').val();
//     var sectionId = $(_this).closest('cart-drawer').attr('section');
//     if(discountCodeValue == ''){
//       _this.closest('.cart-offer-coupon_input').after('<p class="offer_input_err">Please enter any code above.</p>');
//       return;
//     }
//     $(_this).attr("disabled","disabled");
//     $(_this).attr("loading","loading");

//     $('#applied_code_error').remove();
//     applied_code(discountCodeValue, _this, sectionId);
//   });

//   $('body').on('click','.coupon_offer-item_code-copy', function(){
//     var _this  = this;
//     var discountCodeValue = $(this).attr('coupon');
//     var sectionId = $(_this).closest('cart-drawer').attr('section');

//     $(_this).removeAttr('disabled');
//     $(_this).removeAttr('loading');
//     $(_this).removeClass('loading');

//     $('#applied_code_error').remove();
//     applied_code(discountCodeValue, _this, sectionId);
//   });

//   $('body').on('click','[data-removecode]', function(){
//     var _this  = this;
//     console.log('hhiiihi')
//     var sectionId = $(this).closest('cart-drawer').attr('section');
//     var discountCodeValue = $(this).closest('.applied_coupon-item').attr('data-discount-code');
//     var existingDiscountsValue = existingDiscounts();
//     console.log('existingDiscountsValue:', existingDiscountsValue);

//     $(_this).attr("disabled","disabled");
//     $(_this).attr("loading","loading");

//     const index = existingDiscountsValue.indexOf(discountCodeValue);
//     if (index === -1) return;

//     existingDiscountsValue.splice(index, 1);

//     async function getupdatedata(){
//       let response = await fetch(`${window.themeVariables.routes.cart_update_url}.js`, {
//         method: "POST", // HTTP method (GET, POST, PUT, DELETE, etc.)
//         headers : {
//           "Content-Type": "application/json"
//         },
//         body: JSON.stringify({
//           discount: existingDiscountsValue.join(','),
//           sections: [sectionId]
//         })
//       });
//       const data = await response.json();
//       return data;
//     }
//     getupdatedata().then(data => {
//       console.log(data);
//       // const newHtml = data.sections[sectionId];
//       // const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
//       // console.log(parsedHtml);
//       // $('.mini-cart__drawer-footer').html($(parsedHtml).find('.mini-cart__drawer-footer').html());
//       // $('#coupon-offers').html($(parsedHtml).find('#coupon-offers').html());

//       $(_this).removeAttr('disabled');
//       $(_this).removeAttr('loading');
//       $(_this).removeClass('loading');

//       $.getJSON("/cart.js", function (cart) {
//         document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
//           bubbles: true,
//           detail: { cart }
//         }));
//       });
//     });
//   });

function existingDiscounts(){
  const discountCodes = [];
  const discountPills = $('#coupon-offers').find('.cart-discount__pill');
  for (const pill of discountPills) {
    if (typeof $(pill).attr('data-discount-code') === 'string') {
      discountCodes.push($(pill).attr('data-discount-code'));
    }
  }

  return discountCodes;
}

$('body').on('click','[data-inputapply]', function(){
  $('.offer_input_err').remove();
  var _this = $(this);
  _this.attr('disabled','disabled');
  _this.attr('aria-busy','true');
  var sectionId = _this.closest('cart-drawer').attr('section');
  var discountCodeValue = _this.closest('.cart-coupon_offer-input').find('.coupon_code_input').val();
  if(discountCodeValue == ''){
    _this.closest('.cart-coupon_offer-input').after('<p class="offer_input_err">Please enter the code above.</p>');
    _this.removeAttr('disabled','disabled');
    _this.removeAttr('aria-busy','true');
    return;
  }
  var existingDiscountsValue = existingDiscounts();

  if (s6IndexOfCode(existingDiscountsValue, discountCodeValue) !== -1) return;

  async function getupdatedata(){
    let response = await fetch(`${window.themeVariables.routes.cart_update_url}.js`, {
      method: "POST", // HTTP method (GET, POST, PUT, DELETE, etc.)
      headers : {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        discount: [...existingDiscountsValue, discountCodeValue].join(','),
        sections: [sectionId]
      })
    });
    const data = await response.json();
    return data;
  }
  getupdatedata().then(data => {
    s6log(data);
    if (data.discount_codes.find((discount) => {
        return s6SameCode(discount.code, discountCodeValue) && discount.applicable === false;
      })
    ) {
      _this.closest('.cart-coupon_offer-input').after('<p class="offer_input_err">This discount code is not applicable on your cart.</p>');
      _this.removeAttr('disabled','disabled');
      _this.removeAttr('aria-busy','true');
      return;
    }
    if($('body').hasClass('template-cart')){
      window.location.reload();
    }else{
      const newHtml = data.sections[sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      s6log(parsedHtml);
      $('.mini-cart-drawer-content').html($(parsedHtml).find('.mini-cart-drawer-content').html());
      $('.mini-cart__drawer-footer').html($(parsedHtml).find('.mini-cart__drawer-footer').html());
      s6log($('#coupon-offers .cart-coupon_offer-content'));
      $('#coupon-offers .cart-coupon_offer-content').html($(parsedHtml).find('#coupon-offers .cart-coupon_offer-content').html());
    }

  });
});

$('body').on('click','[data-applybutton]', function(){
  var _this = $(this);
  _this.attr('disabled','disabled');
  _this.attr('aria-busy','true');
  var sectionId = $(this).closest('cart-drawer').attr('section');
  var discountCodeValue = $(this).attr('coupon');
  var existingDiscountsValue = existingDiscounts();

  if (s6IndexOfCode(existingDiscountsValue, discountCodeValue) !== -1) return;

  async function getupdatedata(){
    let response = await fetch(`${window.themeVariables.routes.cart_update_url}.js`, {
      method: "POST", // HTTP method (GET, POST, PUT, DELETE, etc.)
      headers : {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        discount: [...existingDiscountsValue, discountCodeValue].join(','),
        sections: [sectionId]
      })
    });
    const data = await response.json();
    return data;
  }
  getupdatedata().then(data => {
    s6log(data);
    if (data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
        return s6SameCode(discount.code, discountCodeValue) && discount.applicable === false;
      })
    ) {
      _this.closest('.cart-coupon_offer_item').append('<p class="offer_input_err">This discount code is not applicable on your cart.</p>');
      setTimeout(function(){
        _this.closest('.cart-coupon_offer_item').find('.offer_input_err').remove();
      },3000);
      _this.removeAttr('disabled','disabled');
      _this.removeAttr('aria-busy','true');
      return;
    }

    if($('body').hasClass('template-cart')){
      window.location.reload();
    }else{
      const newHtml = data.sections[sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      s6log(parsedHtml);
      $('.mini-cart-drawer-content').html($(parsedHtml).find('.mini-cart-drawer-content').html());
      $('.mini-cart__drawer-footer').html($(parsedHtml).find('.mini-cart__drawer-footer').html());
      $('#coupon-offers .cart-coupon_offer-content').html($(parsedHtml).find('#coupon-offers .cart-coupon_offer-content').html());
    }


    // document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
    //   bubbles: true,
    //   detail: {
    //     cart: data
    //   }
    // }));
  });
});

$('body').on('click','[data-removecode]', function(){
  var _this = $(this);
  if(!_this.hasClass('cart-discount__pill-remove')){
    _this.attr('disabled','disabled');
    _this.attr('aria-busy','true');
  }
  var sectionId = $(this).closest('cart-drawer').attr('section');
  var discountCodeValue = $(this).attr('data-discount-code') || $(this).closest('[data-discount-code]').attr('data-discount-code');
  var existingDiscountsValue = existingDiscounts();
  s6log(discountCodeValue)
  const index = s6IndexOfCode(existingDiscountsValue, discountCodeValue);
  if (index === -1) {
    _this.removeAttr('disabled');
    _this.removeAttr('aria-busy');
    return;
  }

  existingDiscountsValue.splice(index, 1);

  async function getupdatedata(){
    let response = await fetch(`${window.themeVariables.routes.cart_update_url}.js`, {
      method: "POST", // HTTP method (GET, POST, PUT, DELETE, etc.)
      headers : {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        discount: existingDiscountsValue.join(','),
        sections: [sectionId]
      })
    });
    const data = await response.json();
    return data;
  }
  getupdatedata().then(data => {
    s6log(data);
    if($('body').hasClass('template-cart')){
      window.location.reload();
    }else{
      const newHtml = data.sections[sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      s6log(parsedHtml);
      $('.mini-cart-drawer-content').html($(parsedHtml).find('.mini-cart-drawer-content').html());
      $('.mini-cart__drawer-footer').html($(parsedHtml).find('.mini-cart__drawer-footer').html());
      s6log($('#coupon-offers .cart-coupon_offer-content'));
      $('#coupon-offers .cart-coupon_offer-content').html($(parsedHtml).find('#coupon-offers .cart-coupon_offer-content').html());
    }

    // document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
    //   bubbles: true,
    //   detail: {
    //     cart: data
    //   }
    // }));
  });
});

function isPartiallyInView($el) {
  var elementTop = $el.offset().top;
  var elementBottom = elementTop + $el.outerHeight();
  var viewportTop = $(window).scrollTop();
  var viewportBottom = viewportTop + $(window).height();

  // Returns true if any part of the element is visible
  return elementBottom > viewportTop && elementTop < viewportBottom;
}

function isFullyOutOfView($el) {
  var elementTop = $el.offset().top;
  var elementBottom = elementTop + $el.outerHeight();
  var viewportTop = $(window).scrollTop();
  var viewportBottom = viewportTop + $(window).height();

  // Returns true if element is entirely above or below the viewport
  return elementBottom <= viewportTop || elementTop >= viewportBottom;
}

function counterFun() {
  var $counter = $('#counter');
  if (!$counter.length) return;

  if (isPartiallyInView($counter)) {
    $('.count').each(function () {
      var $this = $(this);
      var countTo = parseInt($this.attr('data-count'), 10);

      if (!$this.hasClass('counted')) {
        $this.addClass('counted');
        $({ countNum: 0 }).animate(
          { countNum: countTo },
          {
            duration: 2000,
            easing: 'swing',
            step: function () {
              // Add commas while animating
              $this.text(Math.floor(this.countNum).toLocaleString());
            },
            complete: function () {
              // Final value with commas
              $this.text(countTo.toLocaleString());
            }
          }
        );
      }
    });
  } else if (isFullyOutOfView($counter)) {
    // Reset only when fully out of view
    $('.count').each(function () {
      $(this).removeClass('counted').text('0');
    });
  }
}


$(window).on('scroll resize', counterFun);
$(document).ready(counterFun);


$(".benefits-content_wrap .default-line").height($(".benefits-content_wrap").height());
var items = $(".benefits-content_item"),
timelineHeight = $(".benefits-content_wrap").height(),
greyLine = $('.default-line'),
lineToDraw = $('.draw-line');
var liItem = $('.exploring-wellness-content-item');

// sets the height that the greyLine (.default-line) should be according to `.timeline ul` height

// run this function only if draw line exists on the page
if(lineToDraw.length) {
  window.addEventListener('scroll', s6RafThrottle(function () {

    // Need to constantly get '.draw-line' height to compare against '.default-line'
    var redLineHeight = lineToDraw.height(),
    greyLineHeight = greyLine.height(),
    windowDistance = $(window).scrollTop(),
    windowHeight = $(window).height() / 2,
    timelineDistance = $(".benefits-content_wrap-outer").offset().top;

    if(windowDistance >= timelineDistance - windowHeight) {
      line = windowDistance - timelineDistance + windowHeight;

      if(line <= greyLineHeight) {
        lineToDraw.css({
          'height' : line + 'px'
        });
      }
    }

    // This takes care of adding the class in-view to the li:before items
    var bottom = lineToDraw.offset().top + lineToDraw.outerHeight(true);
    items.each(function(index){
      var circlePosition = $(this).offset();

      if(bottom > circlePosition.top) {
        $(this).addClass('in-view');
      } else {
        $(this).removeClass('in-view');
      }
    });
  }), { passive: true });

}

  // ===
  $(".product-featured-item-mobile-list .default-linee").height($(".product-featured-item-mobile-list").height())
  var itemss = $(".product-features-item-m"),
  timelineHeightt = $(".product-featured-item-mobile-list").height(),
  greyLinee = $('.default-linee'),
  lineToDraww = $('.draw-linee');
  if(lineToDraww.length) {
    window.addEventListener('scroll', s6RafThrottle(function () {
      var greyLineHeight = greyLinee.height(),
      windowDistance = $(window).scrollTop(),
      windowHeight = $(window).height() / 2;
      timelineDistance = $(".product-featured-item-mobile-inr").offset().top;
      if(windowDistance >= timelineDistance - windowHeight) {
        line = windowDistance - timelineDistance + windowHeight;

        if(line <= greyLineHeight) {
          lineToDraww.css({
            'height' : line + 'px'
          });
        }
      }
      var bottom = lineToDraww.offset().top + lineToDraww.outerHeight(true);
      itemss.each(function(index){
        var circlePosition = $(this).offset();

        if(bottom > circlePosition.top) {
          $(this).addClass('in-view');
        } else {
          $(this).removeClass('in-view');
        }
      });
    }), { passive: true });
  }

  if ($(window).width() < 767) {
    $('body').on('click','.fhw-iner-item-content', function(){
      $(this).toggleClass('active');
      $(this).find('.fhw-iner-item-content-text').slideToggle();
    });
    $('body').on('click','.fhw-iner-item-header', function(){
      $(this).toggleClass('active');
      var head = $(this).data('head');
      $(this).closest('.fhw-iner').find('.fhw-iner-item').each(function(index, element) {
        var text = $(element).data('text');
        if(head == text) {
           $(element).find('.fhw-iner-item-content').toggleClass('active');
           $(element).find('.fhw-iner-item-content-text').slideToggle({
              complete: function() {
                  // Scroll the content into view after the toggle animation is complete
                  $(element).find('.fhw-iner-item-content-text')[0].scrollIntoView({
                      behavior: 'smooth', // Smooth scrolling animation
                      block: 'center',   // Align to the center of the viewport
                  });
              }
          });
        }
      });
    });
  }

// EXPLORING WELLNESS JS
function revealOnScroll() {
    const revealItems = document.querySelectorAll('.exploring-wellness-content-item');
    const windowHeight = window.innerHeight;
    revealItems.forEach(item => {
        const itemTop = item.getBoundingClientRect().top;
        // Check if the top of the element is within the viewport
        if (itemTop < windowHeight) {
            item.classList.add('in-vieww');
        } else {
            item.classList.remove('in-vieww');
        }
    });
}
window.addEventListener('scroll', s6RafThrottle(revealOnScroll), { passive: true });
revealOnScroll();

/* Swiper is only needed by the .kw-wrap widget, so it is fetched there
   and nowhere else. */
var swiper = null;
if (s6HasAny('.kw-wrap')) {
  s6LoadLib('swiper', 'Swiper', function () {
    swiper = new Swiper(".kw-wrap", {
      effect: "coverflow",
      grabCursor: true,
      centeredSlides: true,
      loop: true,
      slidesPerView: 2,
      coverflowEffect: {
        rotate: 0,
        stretch: 0,
        depth: 100,
        modifier: 4,
        slideShadows: true
      },
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev"
      },
      keyboard: {
        enabled: true
      },
      breakpoints: {
        768: {
          slidesPerView: 3,
          coverflowEffect: false,
          centeredSlides: false
        }
      }
    });
  });
}

/* AOS animates elements that carry a data-aos attribute. No such element
   on the page means the library is never downloaded. */
if (s6HasAny('[data-aos]')) {
  s6LoadLib('aos', 'AOS', function () { AOS.init(); });
}

$('.qv-product-meta-flavours-item-inr').click(function(e){
  e.preventDefault();
  var data_href = $(this).attr('href');
  $(`.qv-product-meta-flavours-item`).removeClass('active');
  $(`.qv-product-meta-flavours-item-inr[href="${data_href}"]`).closest('.qv-product-meta-flavours-item').addClass('active');

  $(`.section[feature_product]`).hide()
  $(`.section[feature_product="${data_href}"]`).show();

  const $el = $(`[feature_product="${data_href}"]`);
  if ($el.length) {
    $('html, body').animate({
      scrollTop: $el.offset().top-60
    }, 100);
  }
});

$("body").on("click", ".product-meta-code_copy-icon", function () {
  var copy_text = $(this);
  var text = copy_text.attr("code"); // <- get value from custom "code" attribute

  if (!text) return; // prevent copying empty

  // Use modern clipboard API if available
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text).then(function () {
      copy_text.attr("copied", "copied");
      setTimeout(function () {
        copy_text.removeAttr("copied");
      }, 1000);
    });
  } else {
    // Fallback for older browsers
    var $temp = $("<input>");
    $("body").append($temp);
    $temp.val(text).select();
    document.execCommand("copy");
    $temp.remove();
    copy_text.attr("copied", "copied");
    setTimeout(function () {
      copy_text.removeAttr("copied");
    }, 1000);
  }
});

$(function () {
  const $buyButtons = $('.product-form__buy-buttons');
  const $stickyForm = $('.product-sticky-form');

  if (!$buyButtons.length || !$stickyForm.length) return;

  function isInViewport($el) {
    const el = $el[0];
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }

  function toggleStickyForm() {
    if (isInViewport($buyButtons)) {
      $stickyForm.attr('hidden', true);
    } else {
      $stickyForm.removeAttr('hidden');
    }
  }

  // Initial check
  toggleStickyForm();

  // Scroll + resize
  $(window).on('scroll resize', toggleStickyForm);
});


// if ($('.marathon-form').length > 0) {
//   $('body').on('submit', '.marathon-form', function (e) {
//     e.preventDefault();

//     var _this = $(this);

//     // hide banners
//     _this.find('.banner--success, .banner--error').hide();

//     // get values
//     var name = _this.find('[name="name"]').val().trim();
//     var email = _this.find('[name="email"]').val().trim();
//     var phoneRaw = _this.find('[name="phone"]').val().trim();
//     var message = _this.find('[name="message"]').val().trim();

//     // simple phone validation (10 digits)
//     var phoneRegex = /^[6-9]\d{9}$/;

//     if (!phoneRegex.test(phoneRaw)) {
//       _this.find('.banner--error').show().find('.banner__content').text('Please enter a valid 10-digit phone number.');
//       return;
//     }

//     // add country code
//     var phone = '91' + phoneRaw;

//     // set loading state
//     _this.find('[type="submit"]').attr('aria-busy', 'true');

//     $.ajax({
//       url: "https://dev.d2cbox.com/shopify/api/v1/create_customer/",
//       method: "POST",
//       contentType: "application/json",
//       headers: {
//         "Authorization": "541d69c00009408dbf39dfe0acfe09df"
//       },
//       data: JSON.stringify({
//         name: name,
//         email: email,
//         phone: phone,
//         message: message,
//         sub_action: 1
//       }),
//       success: function (response) {
//         if (response.status == 1) {
//           _this.find('.banner--success .banner__content').html(`${response.message}`);
//           _this.find('.banner--success').show();
//           _this[0].reset();
//         } else {
//           _this.find('.banner--error .banner__content').html(response.message);
//           _this.find('.banner--error').show();
//         }
//         _this.find('[type="submit"]').attr('aria-busy', 'false');
//         console.log('response',response)
//       },
//       error: function (xhr) {
//         var msg = 'Something went wrong. Please try again.';
//         if (xhr.responseJSON && xhr.responseJSON.detail) {
//           msg = xhr.responseJSON.detail;
//         }

//         _this.find('.banner--error').show().find('.banner__content').text(msg);
//         _this.find('[type="submit"]').attr('aria-busy', 'false');
//         console.log('xhr',xhr);
//       }
//     });
//   });
// }


if ($('.marathon-form').length > 0) {

  // form submit
  $('body').on('submit', '.marathon-form', function (e) {
    e.preventDefault();

    var _this = $(this);

    // hide banners and coupon block
    _this.find('.banner--success, .banner--error').hide();
    _this.find('.marathon-form_code').hide();

    // get values
    var name = _this.find('[name="name"]').val().trim();
    var email = _this.find('[name="email"]').val().trim();
    var phoneRaw = _this.find('[name="phone"]').val();
    // var message = _this.find('[name="message"]').val().trim();

    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      _this.find('[type="submit"]').attr('aria-busy', 'false');
      _this.find('.banner--error').show().find('.banner__content').text('Please enter the valid E-mail ID.');
      return;
    }

    // clean phone (remove spaces, +, -, etc.)
    phoneRaw = phoneRaw.replace(/\D/g, '');
    var phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(phoneRaw)) {
      _this.find('[type="submit"]').attr('aria-busy', 'false');
      _this.find('.banner--error').show().find('.banner__content').text('Please enter the 10 digit mobile number.');
      return;
    }

    // set loading state AFTER validation
    _this.find('[type="submit"]').attr('aria-busy', 'true');

    var phone = '91' + phoneRaw;

    $.ajax({
      url: "https://dev.d2cbox.com/shopify/api/v1/create_customer/",
      method: "POST",
      contentType: "application/json",
      headers: {
        "Authorization": "541d69c00009408dbf39dfe0acfe09df"
      },
      data: JSON.stringify({
        first_name: name,
        email: email,
        phone: phone,
        // message: message,
        tag: 'New Delhi Marathon',
        sub_action:1
      }),
      success: function (response) {


        if (response.status == 1) {

          // success banner
          _this.find('.banner--success .banner__content').html(response.message || 'Success');
          _this.find('.banner--success').show();

          // show coupon code
          if (response.code) {
            _this.find('.marathon-form_code-info_text').text(response.code);

            _this.find('.marathon-form_copy-code').attr('data-code', response.code);

            _this.find('.marathon-form_code').show();
          }

          _this.find('.marathon-form-input_list').hide();
          $('.marathon-show-form_button').show();

          _this[0].reset();

        } else {
          _this.find('.banner--error .banner__content').html(response.message || 'Something went wrong.');
          _this.find('.banner--error').show();
        }
        _this.find('[type="submit"]').attr('aria-busy', 'false');
        s6log('response',response)
      },
      error: function (xhr) {


        var msg = 'Something went wrong. Please try again.';
        if (xhr.responseJSON && xhr.responseJSON.detail) {
          msg = xhr.responseJSON.detail;
        }

        _this.find('.banner--error').show().find('.banner__content').text(msg);
        s6log('xhr',xhr)
        _this.find('[type="submit"]').attr('aria-busy', 'false');
      }
    });
  });

  // copy coupon code
  $('body').on('click', '.marathon-form_copy-code', function () {
    var code = $(this).attr('data-code');
    if (!code) return;

    navigator.clipboard.writeText(code);

    var btn = $(this);
    btn.text('Copied');

    setTimeout(function () {
      btn.text('Copy');
    }, 2000);
  });

  // $('body').on('click', '.marathon-show-form_button', function (e) {
  //   e.preventDefault();
  //   $(this).hide();
  //   $('.marathon-form-input_list').show();
  // });

  // $('[data-listicle-cta-form]').each(function () {
  //   var $form = $(this);
  //   var $hiddenInput = $form.find('[data-listicle-variant-id]');
  //   var $variantLabels = $form.find('[data-listicle-variant]');

  //   $variantLabels.find('input[type="radio"]').on('change', function () {
  //     var $radio = $(this);
  //     console.log('$radio',$radio)
  //     if (!$radio.is(':checked')) return;

  //     var $label = $radio.closest('[data-listicle-variant]');

  //     // update hidden field that actually gets submitted to cart
  //     $hiddenInput.val($radio.val());

  //     // update selected styling
  //     $variantLabels.removeClass('is-selected');
  //     $label.addClass('is-selected');
  //   });
  // });


}

$(document).ready(function () {
  // var $section = $('.listicle-cta');
  // var $form = $section.find('form[action*="/cart/add"]');
  // var $variantId = $form.find('[data-listicle-variant-id]');
  // var $button = $form.find('[data-listicle-add-button]');
  // var $sticky = $section.find('[data-listicle-sticky]');
  // var $stickyButton = $sticky.find('[data-listicle-sticky-add]');
  // var $buttons = $button.add($stickyButton);
  // var buttonLabel = $button.text();
  // var stickyLabel = $stickyButton.text();
  // // var afterAdd = {{ section.settings.after_add | json }};

  // // Sync sticky bar prices with the selected variant
  // function updateSticky() {
  //   if (!$sticky.length) return;

  //   var $checked = $form.find('input[type="radio"]:checked');
  //   if (!$checked.length) return;

  //   var compare = $checked.data('compare');
  //   var save = $checked.data('save');

  //   $sticky.find('[data-sticky-price]').text($checked.data('price'));
  //   $sticky.find('[data-sticky-compare]').text(compare || '');
  //   $sticky.find('[data-sticky-compare-wrap]').toggle(!!compare);
  //   $sticky.find('[data-sticky-save]').text(save || '');
  //   $sticky.find('[data-sticky-save-wrap]').toggle(!!save);
  // }

  // // Variant card selection
  // $form.find('input[type="radio"]').on('change', function () {
  //   $variantId.val($(this).val());
  //   $form.find('[data-listicle-variant]').removeClass('is-selected');
  //   $(this).closest('[data-listicle-variant]').addClass('is-selected');
  //   updateSticky();
  // });

  // updateSticky();

  // // Show sticky bar only when the buy card is out of view
  // function toggleSticky() {
  //   if (!$sticky.length) return;

  //   var card = $section.find('.listicle-cta_card')[0];
  //   if (!card) return;

  //   var rect = card.getBoundingClientRect();
  //   var cardInView = rect.top < window.innerHeight && rect.bottom > 0;

  //   $sticky.toggleClass('is-visible', !cardInView);
  // }

  // $(window).on('scroll resize', toggleSticky);
  // toggleSticky();

  // $('body').on('change', '.listicle-cta_card input[type="radio"]', function () {
  //     var $card = $(this).closest('.listicle-cta_card');

  //     // Remove selected class from all labels in this card
  //     $card.find('.listicle-cta_variant').removeClass('is-selected');

  //     // Add selected class to the checked radio's label
  //     $(this).closest('.listicle-cta_variant').addClass('is-selected');
  // });

  // $('body').on('click', '.listicle-cta_button', function (e) {
  //   e.preventDefault();
  //   $(this).prop('disabled', true);
  //   let $card = $(this).closest('.listicle-cta_card');
  //   let variantId = $card.find('input[type="radio"]:checked').val();
  //   console.log('variantId',variantId);
  //   if (!variantId) {
  //       alert('Please select a variant.');
  //       return;
  //       $(this).prop('disabled', false);
  //   }
  //   $.ajax({
  //       type: 'POST',
  //       url: '/cart/add.js',
  //       data: {
  //           id: variantId,
  //           quantity: 1
  //       },
  //       dataType: 'json',
  //       success: function (response) {
  //         $(this).prop('disabled', false);
  //           console.log('Added to cart', response);
  //           document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
  //           bubbles: true,
  //               detail: {
  //                 cart: data
  //               }
  //             }));
  //       },
  //       error: function (xhr) {
  //           console.error(xhr);
  //       }
  //   });
  // })

  // // Sticky Add To Cart triggers the same form submit
  // $stickyButton.on('click', function () {
  //   $form.trigger('submit');
  // });

  // function resetButtons() {
  //   $buttons.prop('disabled', false);
  //   $button.text(buttonLabel);
  //   $stickyButton.length && $stickyButton.contents().last().replaceWith(' ' + stickyLabel.trim());
  // }


  // Use the Shopify section wrapper — it contains BOTH the <section> and the sticky bar
  var $section = $('.shopify-section--listicle-cta');
  if (!$section.length) return;

  var $form = $section.find('form[action*="/cart/add"]');
  var $variantId = $form.find('[data-listicle-variant-id]');
  var $sticky = $section.find('[data-listicle-sticky]');

  // ---------------------------------------------------------------
  // Sticky bar price sync
  // ---------------------------------------------------------------
  function updateSticky() {
    if (!$sticky.length) return;

    var $checked = $section.find('.listicle-cta_card input[type="radio"]:checked');
    if (!$checked.length) return;

    var compare = $checked.data('compare');
    var save = $checked.data('save');

    $sticky.find('[data-sticky-price]').text($checked.data('price'));
    $sticky.find('[data-sticky-compare]').text(compare || '');
    $sticky.find('[data-sticky-compare-wrap]').toggle(!!compare);
    $sticky.find('[data-sticky-save]').text(save || '');
    $sticky.find('[data-sticky-save-wrap]').toggle(!!save);
  }

  // ---------------------------------------------------------------
  // Variant selection (single delegated handler — survives
  // theme editor section re-renders)
  // ---------------------------------------------------------------
  $('body').on('change', '.listicle-cta_card input[type="radio"]', function () {
    var $card = $(this).closest('.listicle-cta_card');

    $card.find('.listicle-cta_variant').removeClass('is-selected');
    $(this).closest('.listicle-cta_variant').addClass('is-selected');

    $variantId.val($(this).val());
    updateSticky();
  });

  updateSticky();

  // ---------------------------------------------------------------
  // Sticky bar visibility — show only when buy card is out of view
  // ---------------------------------------------------------------
  function toggleSticky() {
    if (!$sticky.length) return;

    var card = $section.find('.listicle-cta_card')[0];
    if (!card) return;

    var rect = card.getBoundingClientRect();
    var cardInView = rect.top < window.innerHeight && rect.bottom > 0;

    $sticky.toggleClass('is-visible', !cardInView);
  }

  $(window).on('scroll resize', toggleSticky);
  toggleSticky();

  // ---------------------------------------------------------------
  // Add to cart (shared by main button + sticky button)
  // ---------------------------------------------------------------
  function setLoading($btn, loading) {
    $btn.prop('disabled', loading);
    // $btn.find('.loader-button__text').toggle(!loading);
    // $btn.find('.loader-button__loader').attr('hidden', loading ? null : '');
  }

  function addToCart($btn) {
    var variantId = $section.find('.listicle-cta_card input[type="radio"]:checked').val() || $variantId.val();

    if (!variantId) {
      alert('Please select a variant.');
      return;
    }

    setLoading($btn, true);

    $.ajax({
      type: 'POST',
      url: '/cart/add.js',
      data: { id: variantId, quantity: 1 },
      dataType: 'json',
      success: function (response) {
        setLoading($btn, false);

        // Refresh through the canonical helper so the event carries the cart
        // object: the milestone free-gift engine (addFreeProduct) ignores a
        // cart:refresh without detail.cart, so gifts were never added from
        // listicle pages. themeRefreshCart(true) also opens the drawer.
        if (typeof window.themeRefreshCart === 'function') {
          window.themeRefreshCart(true);
        } else {
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: { openMiniCart: true }
            })
          );
        }
      },
      error: function (xhr) {
        setLoading($btn, false);

        var message = 'Could not add to cart. Please try again.';
        if (xhr.responseJSON && xhr.responseJSON.description) {
          message = xhr.responseJSON.description;
        }
        alert(message);
      }
    });
  }

  $('body').on('click', '.listicle-cta_button', function (e) {
    e.preventDefault();
    addToCart($(this));
  });

  $('body').on('click', '[data-listicle-sticky-add]', function (e) {
    e.preventDefault();
    addToCart($(this));
  });
});

$(document).ready(function () {


  checkMinQty();
  updatePrice();

$(document).on("click", ".bundle-page-product--quantity_button.minus", function () {
  var $input = $(this).siblings(".bundle-page-product--quantity_input");
  var currentVal = parseInt($input.val());

  if (currentVal > 1) {
    $input.val(currentVal - 1);
    $(".packs_product--list_item--count").text(currentVal - 1);
  }

  checkMinQty();
  updatePrice();
});

$(document).on("click", ".bundle-page-product--quantity_button.plus", function () {
  var $input = $(this).siblings(".bundle-page-product--quantity_input");
  var currentVal = parseInt($input.val());

  $input.val(currentVal + 1);
  $(".packs_product--list_item--count").text(currentVal + 1);
    checkMinQty();
    updatePrice();
  });

  function checkMinQty() {
    $(".bundle-page-product--quantity_input").each(function () {
      var val = parseInt($(this).val());
      var $minus = $(this).siblings(".bundle-page-product--quantity_button.minus");

      if (val <= 1) {
        $minus.addClass("disabled");
      } else {
        $minus.removeClass("disabled");
      }
    });
  }

  function updatePrice() {
    var qty = parseInt($(".bundle-page-product--quantity_input").val()) || 1;

    $(".bundle-page-product--compare_amt").each(function () {
      var basePrice = parseInt($(this).attr("price"));
      $(this).text(formatMoney(basePrice * qty));
    });

    $(".bundle-page-product--sale_price").each(function () {
      var basePrice = parseInt($(this).attr("price"));
      $(this).text(formatMoney(basePrice * qty));
    });

    // Recalculate savings (compare price - sale price)
    var baseCompare = parseInt($(".bundle-page-product--compare_amt").attr("price"));
    var baseSale    = parseInt($(".bundle-page-product--sale_price").attr("price"));
    var savedAmt    = (baseCompare - baseSale) * qty;

    $(".bundle-page-product--save_price").text("(You SAVE " + formatMoney(savedAmt) + ")");
  }

  // $('.packs_product_variant--list').on('click', '.packs_product_variant--list_item--wrapper', function () {
  //   const isActive = $(this).hasClass('active');

  //   $(this).closest('.packs_product_variant--list').find('.packs_product_variant--list_item--wrapper').removeClass('active');

  //   if (!isActive) {
  //     $(this).addClass('active');
  //   }
  // });

  function updateBundleAvailability() {
    var $buttons = $('.bundle-page-product_cta--button, .sticky_bundle-page-product_cta--button');
    if (!$buttons.length) return;

    var soldOut = $('.packs_product.active[data-available="false"]').length > 0;

    var cartIcon = '<svg width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.9708 3.69681C18.7359 3.3908 18.4417 3.14468 18.1093 2.97594C17.7769 2.8072 17.4143 2.71998 17.0473 2.72047H3.54309L3.50801 2.40227C3.43622 1.74088 3.14333 1.13106 2.68487 0.688443C2.22641 0.245822 1.63425 0.00116434 1.02066 0.000854492L0.83524 0.000854492C0.613721 0.000854492 0.401274 0.0963644 0.244636 0.266373C0.0879983 0.436382 0 0.666963 0 0.907392C0 1.14782 0.0879983 1.3784 0.244636 1.54841C0.401274 1.71842 0.613721 1.81393 0.83524 1.81393H1.02066C1.22524 1.81396 1.4227 1.89548 1.57557 2.04303C1.72845 2.19057 1.82612 2.39389 1.85006 2.6144L2.99935 13.2209C3.11866 14.324 3.60697 15.3411 4.37157 16.0792C5.13618 16.8173 6.12386 17.2251 7.14715 17.2251H15.8696C16.0911 17.2251 16.3035 17.1296 16.4602 16.9596C16.6168 16.7895 16.7048 16.559 16.7048 16.3185C16.7048 16.0781 16.6168 15.8475 16.4602 15.6775C16.3035 15.5075 16.0911 15.412 15.8696 15.412H7.14715C6.63018 15.4104 6.12632 15.2353 5.70464 14.9107C5.28295 14.5861 4.9641 14.1279 4.79177 13.5989H14.7478C15.727 13.599 16.675 13.2256 17.4263 12.5441C18.1776 11.8626 18.6844 10.9162 18.8581 9.87033L19.5137 5.92326C19.5792 5.53165 19.5645 5.1292 19.4706 4.74444C19.3768 4.35968 19.2062 4.00202 18.9708 3.69681ZM17.8741 5.60144L17.2176 9.54851C17.1133 10.1767 16.8088 10.7451 16.3572 11.1542C15.9057 11.5632 15.336 11.7868 14.7478 11.7858H4.52617L3.74021 4.53354H17.0473C17.17 4.53275 17.2913 4.5613 17.4027 4.61718C17.5141 4.67305 17.6127 4.75487 17.6917 4.85682C17.7706 4.95877 17.8279 5.07835 17.8594 5.20704C17.891 5.33574 17.896 5.4704 17.8741 5.60144Z" fill="white"></path><path d="M5.84284 21.7565C6.76543 21.7565 7.51333 20.9448 7.51333 19.9434C7.51333 18.9421 6.76543 18.1304 5.84284 18.1304C4.92026 18.1304 4.17236 18.9421 4.17236 19.9434C4.17236 20.9448 4.92026 21.7565 5.84284 21.7565Z" fill="white"></path><path d="M14.1998 21.7565C15.1224 21.7565 15.8703 20.9448 15.8703 19.9434C15.8703 18.9421 15.1224 18.1304 14.1998 18.1304C13.2772 18.1304 12.5293 18.9421 12.5293 19.9434C12.5293 20.9448 13.2772 21.7565 14.1998 21.7565Z" fill="white"></path></svg>';

    $buttons.each(function () {
      var $btn = $(this);
      var $label = $btn.attr('is') === 'loader-button' ? $btn.children().first() : $btn;

      // Capture the original label (icon + text) once, before any change.
      if ($btn.data('atc-default') === undefined) {
        $btn.data('atc-default', $label.html());
      }

      if (soldOut) {
        $label.html(cartIcon + ' SOLD OUT');
      } else {
        $label.html($btn.data('atc-default'));
      }

      $btn.prop('disabled', soldOut).toggleClass('is-sold-out', soldOut);
    });
  }

  updateBundleAvailability();

  $('.packs_product_variant--list').on('click', '.packs_product_variant--list_item--wrapper', function () {
    const isActive = $(this).hasClass('active');
    const $list = $(this).closest('.packs_product_variant--list');
    const $clickedItem = $(this).closest('.packs_product_variant--list_item');

    $list.find('.packs_product_variant--list_item--wrapper').removeClass('active');

    if (!isActive) {
      $(this).addClass('active');
      // Slide up only the non-selected items, leave the active one untouched
      $list.find('.packs_product_variant--list_item').not($clickedItem).slideUp(200);
    } else {
      // Deselecting — slide all others back down
      $list.find('.packs_product_variant--list_item').slideDown(200);
    }
    updateBundleAvailability();
  });

  $('.downstream-effects_list--item').click(function(){
   $(this).toggleClass('active');
  });

  $('.gevwc_wrapper--media').each(function () {
    const $wrapper = $(this);
    const $video = $wrapper.find('video')[0];
    const $btn = $wrapper.find('.gevwc_play_pause_button');

    $btn.on('click', function () {
      if ($video.paused) {
        $video.play();
        $btn.addClass('active');
      } else {
        $video.pause();
        $btn.removeClass('active');
      }
    });

    $($video).on('ended', function () {
      $btn.removeClass('active');
    });
  });

  $('a[href="#probiotics-need"]').on('click', function (e) {
    e.preventDefault();

    const offset = 50;
    const top = $('#probiotics-need').offset().top - offset;

    $('html, body').animate({ scrollTop: top }, 800);
  });

  function probioticsCounterFun() {
    var $counter = $('.probiotics-need-counter_text-block-item_count');
    if (!$counter.length) return;

    if (isPartiallyInView($counter.first())) {
      $counter.each(function () {
        var $span = $(this).find('span[counter]');
        var countTo = parseInt($span.attr('counter'), 10);

        if (!$span.hasClass('counted')) {
          $span.addClass('counted');
          $({ countNum: 0 }).animate(
            { countNum: countTo },
            {
              duration: 2000,
              easing: 'swing',
              step: function () {
                $span.text(Math.floor(this.countNum).toLocaleString());
              },
              complete: function () {
                $span.text(countTo.toLocaleString());
              }
            }
          );
        }
      });
    } else if (isFullyOutOfView($counter.first())) {
      $counter.each(function () {
        $(this).find('span[counter]').removeClass('counted').text('0');
      });
    }
  }

  $(window).on('scroll resize', probioticsCounterFun);
  $(document).ready(probioticsCounterFun);
});


// $('.bundle-page-product_cta--button').on('click', function() {

//   var items = [];
//   var randomId = Math.random().toString(36).substring(2, 12);

//   $('.packs_product.active').each(function(i, e) {
//     items.push({
//       quantity: $('#bundle-page-product--quantity_input').val(),
//       id: $(this).attr('variant_id'),
//     });
//   });

//   console.log('product', items);

//   if ($('.packs_product.active').length > 1) {

//     $('.bundle-page-product_cta--button').attr('disabled', true).attr('aria-busy', true);

//     $.ajax({
//       type: 'POST',
//       url: '/cart/add.js',
//       data: { items },
//       dataType: 'json',
//       error: function(jqXHR, textStatus, errorThrown) {
//         var response = $.parseJSON(jqXHR.responseText);
//         alert(response.description);
//         $('.bundle-page-product_cta--button').prop('disabled', false).removeAttr('aria-busy');
//       },
//       success: function() {
//         console.log('success');
//         $('.bundle-page-product_cta--button').prop('disabled', false).removeAttr('aria-busy');
//       }
//     });

//   } else {
//     var $error = $('.bundle-page-product_qty-cta_error');
//     $error.text('Choose Your Fibre+ Flavour').show();
//     setTimeout(function() {
//       $error.fadeOut();
//     }, 3000);
//   }

// });

const ctaSection = document.querySelector('.bundle-page-product_qty-cta');
const stickyBar = document.querySelector('.sticky_bundle-page-product-cta');

if (ctaSection && stickyBar) {
  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];

      if (entry.isIntersecting) {
        stickyBar.classList.remove('active');
      } else {
        stickyBar.classList.add('active');
      }
    },
    {
      threshold: 0
    }
  );

  observer.observe(ctaSection);
}

$('.bundle-page-product_cta--button, .sticky_bundle-page-product_cta--button').on('click', function() {

  var $buttons = $('.bundle-page-product_cta--button, .sticky_bundle-page-product_cta--button');
  var items = [];

  $('.packs_product.active').each(function() {
    items.push({
      quantity: $('#bundle-page-product--quantity_input').val(),
      id: $(this).attr('variant_id')
    });
  });

  s6log('product', items);

  if ($('.packs_product.active').length > 0) {

    $buttons.prop('disabled', true).attr('aria-busy', true);

    $.ajax({
      type: 'POST',
      url: '/cart/add.js',
      data: { items },
      dataType: 'json',

      error: function(jqXHR) {
        var response = $.parseJSON(jqXHR.responseText);

        alert(response.description);

        $buttons.prop('disabled', false).removeAttr('aria-busy');
      },

      success: function() {
        s6log('success');

        $buttons.prop('disabled', false).removeAttr('aria-busy');

        // Optional: open cart drawer
        // document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        themeRefreshCart(true);
      }
    });

  } else {

    var $error = $('.bundle-page-product_qty-cta_error');

    $error.text('Choose Your Fibre+ Flavour').show();

    setTimeout(function() {
      $error.fadeOut();
    }, 3000);
  }
});

// ---------------------------------------------------------------------------------

const ctaSectionSupply6 = document.querySelector('.ps-atc');
const stickyBarSupply6 = document.querySelector('.sticky_ps-product-cta');

if (ctaSectionSupply6 && stickyBarSupply6) {
  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];

      if (entry.isIntersecting) {
        stickyBarSupply6.classList.remove('active');
      } else {
        stickyBarSupply6.classList.add('active');
      }
    },
    {
      threshold: 0
    }
  );

  observer.observe(ctaSectionSupply6);
}

function updateStickyPrice($option) {
  if (!$option.length) return;

  $('.sticky_ps-product--compare_amt').text(
      $option.find('.ps-old').text().trim()
  );

  $('.sticky_ps-product--sale_price_amt').text(
      $option.find('.ps-new').text().trim()
  );

  $('.sticky_ps-product--save_price_amt').text(
      $option.find('.ps-save').text().trim().toUpperCase()
  );
}

$(document).on('click', '.ps-option', function () {
  $('.ps-option').removeClass('is-selected');
  $(this).addClass('is-selected');
  updateStickyPrice($(this));
});

// Initial load
$(function () {
  updateStickyPrice($('.ps-option.is-selected'));
});

$('.ps-atc,.sticky_ps-product_cta--button').on('click', function(e) {
  e.preventDefault();

  var $button = $(this);
  var variantId = $('.ps-option.is-selected').data('variant-id');

  // Guard: make sure an option is selected
  if (!variantId) {
    alert('Please select an option first.');
    return;
  }

  $button.prop('disabled', true).attr('aria-busy', true);
  s6log(variantId,'variantId')
  $.ajax({
    type: 'POST',
    url: '/cart/add.js',
    data: {
      quantity: 1,
      id: variantId
    },
    dataType: 'json',
    success: function(response) {
      s6log('Added to cart:', response);
      $button.prop('disabled', false).removeAttr('aria-busy');
      // Optional: trigger cart drawer/update here
      // e.g. document.dispatchEvent(new CustomEvent('cart:refresh'));
    },
    error: function(jqXHR) {
      var message = 'Something went wrong. Please try again.';
      try {
        var response = JSON.parse(jqXHR.responseText);
        message = response.description || response.message || message;
      } catch (err) {}
      alert(message);
      $button.prop('disabled', false).removeAttr('aria-busy');
    }
  });
});

(function () {
  var root = document.querySelector('[data-whats-360]');
  if (!root || typeof jQuery === 'undefined') return;
  var $ = jQuery;
  var mq = window.matchMedia('(max-width: 740px)');

  /* ---------------- VIDEO ---------------- */
  var videoWrap = root.querySelector('[data-whats-360-video]');
  if (videoWrap) {
    var video = videoWrap.querySelector('[data-whats-360-video-el]');
    var toggleBtn = videoWrap.querySelector('[data-whats-360-toggle]');

    function play() { var p = video.play(); if (p && p.catch) p.catch(function () {}); }
    function setState(on) {
      videoWrap.classList.toggle('is-playing', on);
      toggleBtn.setAttribute('aria-label', on ? 'Pause video' : 'Play video');
    }
    function toggle() { if (video.paused) play(); else video.pause(); }

    videoWrap.addEventListener('click', toggle);
    toggleBtn.addEventListener('click', function (e) { e.stopPropagation(); toggle(); });
    video.addEventListener('play', function () { setState(true); });
    video.addEventListener('pause', function () { setState(false); });
    video.addEventListener('ended', function () { setState(false); });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting && !video.paused) video.pause();
        });
      }, { threshold: 0.25 }).observe(videoWrap);
    }
  }

  /* ---------------- CARDS: slick (mobile) / scroller (desktop) ---------------- */
  var $cards = $(root).find('[data-whats-360-cards]');
  var dotsHost = root.querySelector('[data-whats-360-dots]');
  var progressBar = root.querySelector('[data-whats-360-progress-bar]');
  var count = $cards.children().length;
  s6Slider(function () {
    $cards.slick({
      slidesToShow: 2,
      slidesToScroll: 1,
      arrows: false,
      dots: true,
      infinite: false,
      appendDots: $(dotsHost)
    });
  });
})();

// -------------------------------------------------------------------------------------------------
(function () {
  var root = document.querySelector('.te');
  if (!root) return;

  /* Swiper is loaded on demand; everything below runs once it is ready. */
  s6LoadLib('swiper', 'Swiper', function () {

    var wrapper = root.querySelector('.swiper-wrapper');
    var quotes = root.querySelectorAll('[data-te-quote]');

    /* 1. Duplicate slides so loop mode never shows a blank slot */
    var originals = Array.prototype.slice.call(wrapper.children);
    if (originals.length > 1) {
      while (wrapper.children.length < Math.max(10, originals.length * 3)) {
        originals.forEach(function (sl) { wrapper.appendChild(sl.cloneNode(true)); });
      }
    }

    /* 2. Fan effect: rotate + drop each card based on distance from center */
    function fan(swiper) {
      var mobile = window.innerWidth < 750;
      var angleStep = mobile ? 5.57 : 3.83;   /* deg per position  */
      var maxAngle  = mobile ? 5.57 : 5.81;   /* rotation cap      */
      var dropStep  = mobile ? 16 : 28;       /* px lower per pos. */
      var maxDrop   = mobile ? 24 : 52;
      swiper.slides.forEach(function (slide) {
        var inner = slide.querySelector('.te__card-inner');
        if (!inner) return;
        var p = slide.progress || 0;  /* 0 = centered, ±1 = next position */
        var angle = Math.max(-maxAngle, Math.min(maxAngle, -p * angleStep));
        var drop  = Math.min(Math.abs(p) * dropStep, maxDrop);
        var scale = Math.abs(p) < 0.5 ? 1 : 0.95;
        inner.style.transform =
          'translateY(' + drop + 'px) rotate(' + angle + 'deg) scale(' + scale + ')';
      });
    }

    /* 3. Videos — queried live every time so Swiper's loop clones are included */
    function pauseAll(except) {
      root.querySelectorAll('video').forEach(function (v) {
        if (v !== except) v.pause();
      });
    }

    /* Delegated: works on originals AND on clones Swiper creates after init */
    root.addEventListener('click', function (e) {
      var card = e.target.closest('[data-te-card]');
      if (!card) return;
      if (swiper && !swiper.allowClick) return;   /* it was a drag, not a click */

      var video = card.querySelector('video');
      if (!video) return;

      if (video.paused) {
        pauseAll(video);                          /* stop whatever else is playing */
        var p = video.play();
        if (p) p.catch(function () {});            /* ignore rapid-click AbortError */
      } else {
        video.pause();
      }
    });

    /* Delegated too — play/pause fire on the element, so capture phase */
    root.addEventListener('play', function (e) {
      var card = e.target.closest('[data-te-card]');
      if (card) card.classList.add('is-playing');
    }, true);

    root.addEventListener('pause', function (e) {
      var card = e.target.closest('[data-te-card]');
      if (card) card.classList.remove('is-playing');
    }, true);

    root.addEventListener('ended', function (e) {
      var card = e.target.closest('[data-te-card]');
      if (card) card.classList.remove('is-playing');
    }, true);

    /* 4. Quote under the slider follows the centered card */
    function setQuote(i) {
      quotes.forEach(function (q, n) {
        q.classList.toggle('is-active', n === i % quotes.length);
      });
    }

    var swiper = new Swiper(root.querySelector('.swiper'), {
      slidesPerView: 'auto',
      centeredSlides: true,
      loop: originals.length > 1,
      speed: 500,
      grabCursor: true,
      watchSlidesProgress: true,
      on: {
        init: fan,
        resize: fan,
        setTranslate: fan,
        slideChangeTransitionStart: function () { pauseAll(); },
        slideChange: function (s) { setQuote(s.realIndex); }
      }
    });

    /* 5. Arrows */
    var prev = root.querySelector('[data-te-prev]');
    var next = root.querySelector('[data-te-next]');
    if (prev) prev.addEventListener('click', function () { swiper.slidePrev(); });
    if (next) next.addEventListener('click', function () { swiper.slideNext(); });

    /* 6. Pause everything when the section scrolls out of view */
    new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) pauseAll();
    }, { threshold: 0.1 }).observe(root);
  });
})();



/* =========================================================
  Fibre+ product page
  - Sticky CTA reveal (IntersectionObserver on the main ATC)
  - Variant card selection + sticky price sync
  - AJAX add-to-cart (main button + sticky button)
  Requires jQuery (already bundled in the theme).
  ========================================================= */

/* ---------- Sticky CTA: show when the main ATC scrolls out of view ---------- */
const fibreAtc = document.querySelector('.fibre-pdp__atc');
const fibreSticky = document.querySelector('.fibre-sticky');

if (fibreAtc && fibreSticky) {
const observer = new IntersectionObserver(
  (entries) => {
    const entry = entries[0];
    if (entry.isIntersecting) {
      fibreSticky.classList.remove('is-active');
    } else {
      fibreSticky.classList.add('is-active');
    }
  },
  { threshold: 0 }
);

observer.observe(fibreAtc);
}

/* ---------- Copy selected card's prices into the sticky bar ---------- */
function updateFibreStickyPrice($card) {
if (!$card || !$card.length) return;

$('[data-sticky-compare]').text($card.attr('data-compare') || '');
$('[data-sticky-price]').text($card.attr('data-price') || '');
$('[data-sticky-save]').text('SAVE ' + ($card.attr('data-save') || ''));
}

/* ---------- Variant card selection ---------- */
$(document).on('click', '.fibre-pdp__variant', function () {
const $card = $(this);
if ($card.attr('data-available') !== 'true') return; // ignore sold-out

$('.fibre-pdp__variant').removeClass('is-selected');
$card.addClass('is-selected');

// keep the form's hidden input in sync as a fallback
$('[data-variant-input]').val($card.attr('data-variant-id'));

updateFibreStickyPrice($card);
});

/* ---------- Initial load ---------- */
$(function () {
updateFibreStickyPrice($('.fibre-pdp__variant.is-selected'));
});

/* ---------- AJAX add-to-cart (main + sticky buttons) ---------- */
$(document).on('click', '.fibre-pdp__atc, .fibre-sticky__btn', function (e) {
  e.preventDefault();

  const atc_button = $(this);
  const variantId = $('.fibre-pdp__variant.is-selected').data('variant-id');

  // Guard: make sure a variant is selected
  if (!variantId) {
    alert('Please select an option first.');
    return;
  }

  atc_button.prop('disabled', true).attr('aria-busy', true);
  s6log(atc_button,'atc_button');
  s6log(variantId,'variantId');
  $.ajax({
    type: 'POST',
    url: '/cart/add.js',
    data: { quantity: 1, id: variantId },
    dataType: 'json',
    error: function(jqXHR, textStatus, errorThrown){
      var description = '';
      try { description = ($.parseJSON(jqXHR.responseText) || {}).description; } catch (e) {}
      console.error('Add to cart failed', description || textStatus || errorThrown);
      atc_button.prop('disabled', false).removeAttr('aria-busy');
    },
    success: function() {
      s6log('success');
      // was: document.dispatchEvent(new CustomEvent('cart:refresh')) - no detail.cart,
      // so the milestone gift engine skipped this add entirely.
      themeRefreshCart(false);
      atc_button.prop('disabled', false).removeAttr('aria-busy');
    }
  });
// $.ajax({
//   type: 'POST',
//   url: '/cart/add.js',
//   data: { quantity: 1, id: variantId },
//   dataType: 'json',
//   success: function (response) {
//     console.log('Added to cart:', response);
//     $button.prop('disabled', false).removeAttr('aria-busy');
//     // Refresh Focal's cart drawer / count
//     document.dispatchEvent(new CustomEvent('cart:refresh'));
//   },
//   error: function (jqXHR) {
//     let message = 'Something went wrong. Please try again.';
//     try {
//       const response = JSON.parse(jqXHR.responseText);
//       message = response.description || response.message || message;
//     } catch (err) {
//       alert(message);
//     }
//     // alert(message);
//     $button.prop('disabled', false).removeAttr('aria-busy');
//   }
// });
});


/* =========================================================
  "What's 360" section — video + cards carousel.
  Unrelated to the Fibre+ product hero; kept as-is so this
  file stays complete. Targets its own [data-whats-360] markup.
  ========================================================= */
// (function () {
//   var root = document.querySelector('[data-whats-360]');
//   if (!root || typeof jQuery === 'undefined') return;
//   var $ = jQuery;

//   /* ---------------- VIDEO ---------------- */
//   var videoWrap = root.querySelector('[data-whats-360-video]');
//   if (videoWrap) {
//     var video = videoWrap.querySelector('[data-whats-360-video-el]');
//     var toggleBtn = videoWrap.querySelector('[data-whats-360-toggle]');

//     function play() { var p = video.play(); if (p && p.catch) p.catch(function () {}); }
//     function setState(on) {
//       videoWrap.classList.toggle('is-playing', on);
//       toggleBtn.setAttribute('aria-label', on ? 'Pause video' : 'Play video');
//     }
//     function toggle() { if (video.paused) play(); else video.pause(); }

//     videoWrap.addEventListener('click', toggle);
//     toggleBtn.addEventListener('click', function (e) { e.stopPropagation(); toggle(); });
//     video.addEventListener('play', function () { setState(true); });
//     video.addEventListener('pause', function () { setState(false); });
//     video.addEventListener('ended', function () { setState(false); });

//     if ('IntersectionObserver' in window) {
//       new IntersectionObserver(function (entries) {
//         entries.forEach(function (entry) {
//           if (!entry.isIntersecting && !video.paused) video.pause();
//         });
//       }, { threshold: 0.25 }).observe(videoWrap);
//     }
//   }
// })();

// (function () {
//     $('[data-whats-360]').each(function () {
//       var $root   = $(this);
//       var $video  = $root.find('[data-whats-360-video]');
//       var $el     = $root.find('[data-whats-360-video-el]');
//       var $poster = $root.find('[data-whats-360-poster]');
//       var video   = $el.get(0);
//       if (!video) return;

//       $root.find('[data-whats-360-toggle]').on('click', function () {
//         if (video.paused) {
//           video.play();
//           $poster.hide();               // hide poster once playing
//           $video.addClass('is-playing');
//         } else {
//           video.pause();
//           $video.removeClass('is-playing');
//         }
//       });

//       // keep icon state in sync if video ends / is controlled elsewhere
//       $el.on('play',  function () { $video.addClass('is-playing'); $poster.hide(); });
//       $el.on('pause', function () { $video.removeClass('is-playing'); });
//     });
//   })();

(function () {
  // Toggle playback — delegated, resolves the target relative to the clicked button.
  $(document).on('click', '[data-whats-360-toggle]', function () {
    var video = $(this).closest('[data-whats-360-video]')
                       .find('[data-whats-360-video-el]').get(0);
    if (!video) return;
    video.paused ? video.play() : video.pause();
  });

  // UI state follows the *actual* media state.
  // play/pause don't bubble, so bind these directly to each video (not delegated).
  $(function () {
    $('[data-whats-360-video-el]').each(function () {
      var video   = this;
      var $wrap   = $(video).closest('[data-whats-360-video]');
      var $poster = $wrap.find('[data-whats-360-poster]');
      video.addEventListener('play',  function () { $wrap.addClass('is-playing'); $poster.hide(); });
      video.addEventListener('pause', function () { $wrap.removeClass('is-playing'); });
    });
  });
})();

$(document).ready(function () {
  $('.whats-360').each(function () {
    var $section = $(this);
    var $cards = $section.find('[data-whats-360-cards]');
    var $dotsHost = $section.find('[data-whats-360-dots]');

    s6Slider(function () {
      if ($cards.length && !$cards.hasClass('slick-initialized')) {
        $cards.slick({
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          infinite: false,
          appendDots: $dotsHost
        });
      }
    });
  });
});

$(document).ready(function() {
  (function ($) {
    'use strict';

    var NAMESPACE = '.nutritionSearch';

    function init(container) {
      var $section = $(container);
      var $field = $section.find('.nutrition-search__field');
      var $chipLabel = $section.find('.nutrition-search__chip-label');
      var $clear = $section.find('.nutrition-search__clear');
      var $pills = $section.find('.nutrition-search__pill');
      var $panels = $section.find('.nutrition-search__panel');

      // 1. Fill the field with a goal's label and reveal that goal's products.
      //    The --selected class on the section is what hides the pill row.
      function select($pill) {
        $pills.removeClass('nutrition-search__pill--selected').attr('aria-pressed', 'false');
        $pill.addClass('nutrition-search__pill--selected').attr('aria-pressed', 'true');

        $chipLabel.text($pill.data('label'));
        $field.addClass('nutrition-search__field--filled');

        $panels.removeClass('nutrition-search__panel--active');
        $panels.filter('[data-panel="' + $pill.data('panel') + '"]').addClass('nutrition-search__panel--active');

        $section.addClass('nutrition-search--selected');
      }

      // 2. Reset to the placeholder, hide the products, bring the pills back.
      function clear() {
        $pills.removeClass('nutrition-search__pill--selected').attr('aria-pressed', 'false');

        $chipLabel.text('');
        $field.removeClass('nutrition-search__field--filled');

        $panels.removeClass('nutrition-search__panel--active');

        $section.removeClass('nutrition-search--selected');
      }

      // 3. A pill selects itself, or clears if it was already the active one.
      $pills.on('click' + NAMESPACE, function () {
        var $pill = $(this);

        if ($pill.hasClass('nutrition-search__pill--selected')) {
          clear();
        } else {
          select($pill);
        }
      });

      // 4. The x button always clears.
      $clear.on('click' + NAMESPACE, clear);
    }

    function destroy(container) {
      $(container).find('.nutrition-search__pill, .nutrition-search__clear').off(NAMESPACE);
    }

    // 5. Boot every instance on the page.
    $(document).ready(function () {
      $('.nutrition-search').each(function () {
        init(this);
      });
    });

    // 6. Theme editor support — always destroy before re-initialising.
    $(document).on('shopify:section:load', function (event) {
      $(event.target).find('.nutrition-search').each(function () {
        destroy(this);
        init(this);
      });
    });

    $(document).on('shopify:section:unload', function (event) {
      $(event.target).find('.nutrition-search').each(function () {
        destroy(this);
      });
    });
  })(jQuery);

   var PREV_ARROW =
    '<button type="button" class="wellness-routine__arrow wellness-routine__arrow--prev" aria-label="Previous">' +
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 4 7 12l8 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
    '</button>';

  var NEXT_ARROW =
    '<button type="button" class="wellness-routine__arrow wellness-routine__arrow--next" aria-label="Next">' +
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 4l8 8-8 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
    '</button>';

    // 1. Slick is loaded on demand - this only configures it.
    s6Slider(function () {
      var $track = $('.wellness-routine__track');
      $track.slick({
        slidesToShow: 3,
        slidesToScroll: 1,
        infinite: true,
        dots: true,
        arrows: true,
        prevArrow: PREV_ARROW,
        nextArrow: NEXT_ARROW,
        responsive: [
          {
            breakpoint: 1000,
            settings: { slidesToShow: 2 }
          },
          {
            breakpoint: 700,
            settings: { slidesToShow: 1, arrows: true }
          }
        ]
      });
    });

    const section = document.querySelector('.listicle-duo-bundle_cta--button');
    const stickyBar = document.querySelector('.listicle-duo-bundle__sticky');

    if (section && stickyBar) {
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          // Section is visible → Hide sticky
          stickyBar.classList.remove('is-active');
        } else {
          // Section is not visible → Show sticky
          stickyBar.classList.add('is-active');
        }
      }, {
        threshold: 0
      });

      observer.observe(section);
    }

  $('body').on('click', '.listicle-hero-baner-link', function (e) {
    e.preventDefault();
    const target = $(this).attr('href');
    if ($(target).length) {
      $('html, body').animate({
        scrollTop: $(target).offset().top - 73
      }, 600);
    }
  });

})

$(document).on('click', '.listicle-hero-banr-btn', function (e) {
  e.preventDefault();
  const atc_button = $(this);
  const variantId = $(this).attr('variant_id');
  s6log('variantId',variantId);

  // Guard: make sure a variant is selected
  if (!variantId) {
    alert('Product is not added');
    return;
  }
  atc_button.prop('disabled', true).attr('aria-busy', true);
  s6log(atc_button,'atc_button');
  s6log(variantId,'variantId');
  $.ajax({
    type: 'POST',
    url: '/cart/add.js',
    data: { quantity: 1, id: variantId },
    dataType: 'json',
    error: function(jqXHR, textStatus, errorThrown){
      var description = '';
      try { description = ($.parseJSON(jqXHR.responseText) || {}).description; } catch (e) {}
      console.error('Add to cart failed', description || textStatus || errorThrown);
      atc_button.prop('disabled', false).removeAttr('aria-busy');
    },
    success: function() {
      s6log('success');
      // was: document.dispatchEvent(new CustomEvent('cart:refresh')) - no detail.cart,
      // so the milestone gift engine skipped this add entirely.
      themeRefreshCart(false);
      atc_button.prop('disabled', false).removeAttr('aria-busy');
    }
  });
});

$(document).on('click', '.enery-listicle-banr-btn', function (e) {
  e.preventDefault();
  const atc_button = $(this);
  const variantId = $(this).attr('variant_id');
  s6log('variantId',variantId);

  // Guard: make sure a variant is selected
  if (!variantId) {
    alert('Product is not added');
    return;
  }
  atc_button.prop('disabled', true).attr('aria-busy', true);
  s6log(atc_button,'atc_button');
  s6log(variantId,'variantId');
  $.ajax({
    type: 'POST',
    url: '/cart/add.js',
    data: { quantity: 1, id: variantId },
    dataType: 'json',
    error: function(jqXHR, textStatus, errorThrown){
      var description = '';
      try { description = ($.parseJSON(jqXHR.responseText) || {}).description; } catch (e) {}
      console.error('Add to cart failed', description || textStatus || errorThrown);
      atc_button.prop('disabled', false).removeAttr('aria-busy');
    },
    success: function() {
      s6log('success');
      // was: document.dispatchEvent(new CustomEvent('cart:refresh')) - no detail.cart,
      // so the milestone gift engine skipped this add entirely.
      themeRefreshCart(false);
      atc_button.prop('disabled', false).removeAttr('aria-busy');
    }
  });
});

$(document).ready(function() {

  function eneryListiclecounter() {
    $('.enery-listicle-bnr-block-inr').each(function () {

      var $span = $(this).find('span[counter]');
      if (!$span.length) return;

      var countTo = parseInt($span.attr('counter'), 10);

      if (isPartiallyInView($(this)) && !$span.hasClass('counted')) {

        $span.addClass('counted');
        $span.text('0');

        $({ Counter: 0 }).animate(
          { Counter: countTo },
          {
            duration: 2000,
            easing: 'swing',
            step: function () {
              $span.text(Math.ceil(this.Counter));
            },
            complete: function () {
              $span.text(countTo);
            }
          }
        );

      } else if (isFullyOutOfView($(this))) {

        $span.removeClass('counted');
        $span.text('0');

      }

    });
  }

  $(window).on('load scroll resize', eneryListiclecounter);

  $('body').on('click', '.enery-listicle-jump-item', function(e) {
    e.preventDefault();
    var target = $($(this).attr('href'));
    if (target.length) {
        $('html, body').animate({
            scrollTop: target.offset().top - 100 // Adjust for sticky header
        }, 600);
    }
  });
  $('body').on('click', '.enery-listicle-baner-link', function(e) {
    e.preventDefault();
    var target = $($(this).attr('href'));
    if (target.length) {
        $('html, body').animate({
            scrollTop: target.offset().top
        }, 600);
    }
  });

  if (window.innerWidth <= 999) {
    s6Slider(function () {
      const $cards = $('.enery-listicle-variety-cards');
      if ($cards.length && !$cards.hasClass('slick-initialized')) {
        $cards.slick({
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          infinite: false
        });
      }
    });
  }

  if (window.innerWidth <= 999) {
    s6Slider(function () {
      const $cardss = $('.enery-customer-reviews-grid');
      if ($cardss.length && !$cardss.hasClass('slick-initialized')) {
        $cardss.slick({
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          infinite: true
        });
      }
    });
  }

})

/* Cart drawer announcement strip.
   The strip is now a CSS scroll-snap row (see the {% style %} block in
   sections/mini-cart.liquid), so no carousel library is needed. All this does
   is nudge it along every few seconds.
   The drawer re-renders by replacing its innerHTML, so we re-arm from the
   cart:refresh hook; data-s6-ann marks a strip that is already running. */
var s6AnnTimer = null;

function initCartAnnStrip() {
  var el = document.querySelector('.cart-drawer-ann-bar-blocks');
  if (!el) return;
  if (el.dataset.s6Ann === '1') return;   /* this strip is already rotating */
  el.dataset.s6Ann = '1';

  /* A re-rendered drawer means a brand new element, so stop the old timer. */
  if (s6AnnTimer) { clearInterval(s6AnnTimer); s6AnnTimer = null; }

  var count = el.children.length;
  if (count < 2) return;                  /* one message: nothing to rotate */

  var timer = setInterval(function () {
    /* The drawer was re-rendered and this element is gone - stop. */
    if (!el.isConnected) { clearInterval(timer); return; }
    var step = el.clientWidth || 1;
    var next = Math.round(el.scrollLeft / step) + 1;
    if (next >= count) next = 0;
    el.scrollTo({ left: next * step, behavior: 'smooth' });
  }, 3000);

  el._s6AnnTimer = timer;
  s6AnnTimer = timer;
}

document.addEventListener('cart:refresh', function(event) {
  // setTimeout 0: run after the drawer DOM swap that follows this event.
  setTimeout(initCartAnnStrip, 0);
})

$(window).on('load', function () {
  setTimeout(initCartAnnStrip, 0);
})

$(document).ready(function () {

  $('body').on('click', '.view-bill-details', function(e) {
    e.preventDefault();
    var $container = $('.mini-cart-drawer-content');
    var $target = $('#cart-drawer-order-summary');
    if (!$container.length || !$target.length) return;
    var scrollTop = $target.position().top + $container.scrollTop() - 60;
    $container.stop(true, true).animate({
        scrollTop: scrollTop
    }, 800);
  });

  // $('body').on('click', '[data-remove-request]', function (e) {
  //   e.preventDefault();
  //   $(this).closest('.line-item').addClass('is-removing');
  // });

  // $('body').on('click', '[data-remove-undo]', function (e) {
  //   e.preventDefault();
  //   $(this).closest('.line-item').removeClass('is-removing');
  // });

  // $('body').on('click', '[data-remove-request]', function (e) {
  //   e.preventDefault();
  //   $(this).closest('.line-item').addClass('is-removing');
  // });

  // $('body').on('click', '[data-remove-undo]', function (e) {
  //   e.preventDefault();
  //   $(this).closest('.line-item').removeClass('is-removing');
  // });

  // $('body').on('click', '[data-remove-confirm]', function (e) {
  //   e.preventDefault();
  //   var $btn = $(this);
  //   if ($btn.attr('aria-busy') === 'true') return;

  //   $btn.attr('aria-busy', 'true');

  //   var removeLink = $btn.closest('.line-item').find('[data-remove-link]')[0];
  //   if (removeLink) {
  //     removeLink.click();
  //   }
  // });

  // CART CLAIM PRODUCT
  $('body').on('click', '[data-claim-add]', function (e) {
    e.preventDefault();

    var $btn = $(this);
    if ($btn.closest('.claim-products').hasClass('is-locked')) return;
    if ($btn.attr('aria-busy') === 'true') return;

    var variantId = $btn.closest('.claim-card').attr('data-claim-variant');
    if (!variantId) return;

    $btn.attr('aria-busy', 'true');

    $.ajax({
      type: 'POST',
      url: '/cart/add.js',
      dataType: 'json',
      data: {
        items: [{ id: variantId, quantity: 1, properties: { _claim: 'true' } }]
      },
      success: function () {
          themeRefreshCart(true);
        },
      error: function (jqXHR) {
        var message = 'Could not claim this product. Please try again.';
        try {
          message = JSON.parse(jqXHR.responseText).description || message;
        } catch (err) {}
        alert(message);
        $btn.removeAttr('aria-busy');
      }
    });
  });

  // =============================
  $('body').on('click', '[data-product-add]', function (e) {
    e.preventDefault();

    var $btn = $(this);
    if ($btn.closest('.is-locked').length) return;
    if ($btn.attr('aria-busy') === 'true') return;

    var variantId = $btn.closest('[data-add-variant]').attr('data-add-variant');
    if (!variantId) return;

    var item = { id: variantId, quantity: 1 };
    if ($btn.closest('.claim-products').length) {
      item.properties = { _claim: 'true' };
    }

    $btn.attr('aria-busy', 'true');

    $.ajax({
      type: 'POST',
      url: '/cart/add.js',
      dataType: 'json',
      data: { items: [item] },
      success: function () {
          themeRefreshCart(true);
        },
      error: function (jqXHR) {
        var message = 'Could not add this product. Please try again.';
        try {
          message = JSON.parse(jqXHR.responseText).description || message;
        } catch (err) {}
        alert(message);
        $btn.removeAttr('aria-busy');
      }
    });
  });

  $('body').on('click', '.cart-routine__tab', function () {
    var $tab = $(this);
    var $routine = $tab.closest('.cart-routine');
    var index = $tab.attr('data-routine-tab');

    $routine.find('.cart-routine__tab').removeClass('is-active');
    $tab.addClass('is-active');

    $routine.find('.cart-routine__panel').removeClass('is-active');
    $routine.find('.cart-routine__panel[data-routine-panel="' + index + '"]').addClass('is-active');
  });

  document.addEventListener("variant:changed", function (event) {
    let variant = event.detail.variant;
    let form = event.target;
    var product_handle = $("body").attr("product-handle");
    $.get(`/products/${product_handle}?variant=${variant.id}`, function (data) {
      $(".product-meta__serving-text_wrapper")?.html(
        $(data).find(".product-meta__serving-text_wrapper").html()
      );
    });
  });

})


$(function () {
  var bar = document.querySelector('[data-view-cart-bar]');
  if (!bar) return;

  var miniCart = document.getElementById('mini-cart');

  // Neutralise the add-to-cart auto-open before the cart-drawer handler sees it.
  // document.addEventListener('cart:refresh', function (e) {
  //   if (e && e.detail && e.detail.openMiniCart) {
  //     e.detail.openMiniCart = false;
  //   }
  // }, true);

  // Bar opens the drawer.
  function openCart() {
    if (miniCart) miniCart.open = true;
  }

  $(document).on('click', '[data-view-cart-bar]', openCart);
  $(document).on('keydown', '[data-view-cart-bar]', function (e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openCart();
    }
  });

  // Keep the bar in sync with the cart.
  function sized(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function renderBar(cart) {
    if (!cart) return;
    var $bar = $(bar);

    if (!cart.item_count) {
      $bar.addClass('is-hidden');
      return;
    }

    var thumbs = '';
    cart.items.slice(0, 3).forEach(function (item) {
      thumbs += '<span class="view-cart-bar__thumb"><img src="' + sized(item.image, 120) + '" alt="" loading="lazy"></span>';
    });

    $bar.find('.view-cart-bar__thumbs').html(thumbs);
    $bar.find('.view-cart-bar__count').text(cart.item_count + (cart.item_count === 1 ? ' Item' : ' Items'));
    $bar.removeClass('is-hidden');
  }

  document.documentElement.addEventListener('cart:updated', function (e) {
    renderBar(e.detail && e.detail.cart);
  });

  document.documentElement.addEventListener('cart:refresh', function (e) {
    if (e.detail && e.detail.cart) renderBar(e.detail.cart);
  });
});


// MILESTONE BASE GIFT ADDED
function addFreeProduct(event, cart) {
  const settings = window.themeVariables?.settings;
  if (!settings) return;

  const freeProductId1 = settings?.freeProduct_1?.variants?.[0]?.id;
  const milestone1 = parseInt(settings?.freeProduct_milestone_1 || "0") * 100;

  const freeProductId2 = settings?.freeProduct_2?.variants?.[0]?.id;
  const milestone2 = parseInt(settings?.freeProduct_milestone_2 || "0") * 100;

  const total = cart.original_total_price;

  const line1 = cart.items.findIndex(item => item.variant_id === freeProductId1);
  const line_key_1 = cart.items[line1];

  const line2 = cart.items.findIndex(item => item.variant_id === freeProductId2);
  const line_key_2 = cart.items[line2];

  var items = [];

  // Add gift 1 when milestone reached and not already in cart
  if (total >= milestone1 && line1 === -1 && freeProductId1) {
    items.push({ quantity: 1, id: freeProductId1, properties: { '_type': 'free_gift' } });
  }
  // Add gift 2 when milestone reached and not already in cart
  if (total >= milestone2 && line2 === -1 && freeProductId2) {
    items.push({ quantity: 1, id: freeProductId2, properties: { '_type': 'free_gift' } });
  }

  // Remove gifts when total drops below their milestone
  var updateData = {};
  if (total < milestone1 && line1 !== -1) { updateData[line_key_1.key] = 0; }
  if (total < milestone2 && line2 !== -1) { updateData[line_key_2.key] = 0; }

  // Both writes used to be synchronous XHRs, which froze the tab for the
  // whole round trip (and could fire back to back). They are now async and
  // chained: add -> update -> one refresh, so the refresh always happens
  // after the cart write has actually landed.
  if (items.length > 0) {
    $.ajax({
      type: 'POST',
      url: '/cart/add.js',
      data: { items },
      dataType: 'json'
    })
      .done(function () { runGiftUpdates(true); })
      .fail(function (jqXHR, textStatus) {
        var description = '';
        try { description = ($.parseJSON(jqXHR.responseText) || {}).description; } catch (e) {}
        console.error('Free Gift Error', description || textStatus);
        runGiftUpdates(false);
      });
  } else {
    runGiftUpdates(false);
  }

  function runGiftUpdates(didAdd) {
    if (Object.keys(updateData).length > 0) {
      $.ajax({
        type: 'POST',
        url: '/cart/update.js',
        data: { updates: updateData },
        dataType: 'json'
      })
        .done(function () { refreshCart(); })
        .fail(function (jqXHR) {
          console.error('Free Gift removal failed', jqXHR && jqXHR.status);
          if (didAdd) refreshCart();
        });
    } else if (didAdd) {
      refreshCart();
    }
  }

  function refreshCart() {
    if (document.body.classList.contains("template-cart")) {
      location.reload();
    } else {
      // themeRefreshCart is the canonical helper: it re-fetches /cart.js and
      // dispatches cart:updated + cart:refresh with detail.cart.
      themeRefreshCart(false);
    }
  }
}

// Trigger on every cart change
document.addEventListener('cart:refresh', function (event) {
  var cart = event.detail && event.detail.cart;
  if (!cart) return;
  addFreeProduct(event, cart);
});
// (function () {
//   var pending = false;

//   function parseGiftMapFromHtml(html) {
//     var fakeDiv = document.createElement('div');
//     fakeDiv.innerHTML = html;
//     var maps = [];
//     fakeDiv.querySelectorAll('[data-milestone-gift-map]').forEach(function (el) {
//       try {
//         var parsed = JSON.parse(el.getAttribute('data-milestone-gift-map') || '[]');
//         maps = maps.concat(parsed);
//       } catch (e) {}
//     });
//     return maps;
//   }

//   function getSectionId() {
//     var miniCart = document.getElementById('mini-cart');
//     return miniCart ? miniCart.getAttribute('section') : 'mini-cart';
//   }

//   async function reconcileMilestoneGifts() {
//     if (pending) return;
//     pending = true;

//     try {
//       var cartUrl = window.themeVariables.routes.cartUrl;
//       var sectionId = getSectionId();

//       // Fetch a freshly-rendered copy of the section ourselves, rather than
//       // trusting the live DOM — cart-drawer.js's own re-render is async and
//       // can still be in flight (it does its own extra fetch whenever the
//       // triggering AJAX call didn't request `sections`), so reading the DOM
//       // directly is a race. This sidesteps that entirely.
//       var [sectionResponse, cartResponse] = await Promise.all([
//         fetch(cartUrl + '?section_id=' + sectionId),
//         fetch(cartUrl + '.js')
//       ]);
//       var html = await sectionResponse.text();
//       var cart = await cartResponse.json();
//       var entries = parseGiftMapFromHtml(html);

//       if (!entries.length) return;

//       var byLevel = {};
//       entries.forEach(function (entry) { byLevel[entry.level] = entry; });

//       var toAdd = [];
//       var toRemove = [];

//       Object.keys(byLevel).forEach(function (level) {
//         var entry = byLevel[level];
//         var lineItem = cart.items.find(function (item) {
//           return item.variant_id === entry.variantId
//             && item.properties
//             && parseInt(item.properties._milestone_gift_level, 10) === parseInt(level, 10);
//         });

//         if (entry.achieved && !lineItem) {
//           toAdd.push({ id: entry.variantId, quantity: 1, properties: { _milestone_gift_level: level } });
//         }
//         if (!entry.achieved && lineItem) {
//           toRemove.push(lineItem.key);
//         }
//       });

//       if (!toAdd.length && !toRemove.length) return;

//       for (var i = 0; i < toRemove.length; i++) {
//         await fetch('/cart/change.js', {
//           method: 'POST',
//           headers: { 'Content-Type': 'application/json' },
//           body: JSON.stringify({ id: toRemove[i], quantity: 0 })
//         });
//       }
//       if (toAdd.length) {
//         await fetch('/cart/add.js', {
//           method: 'POST',
//           headers: { 'Content-Type': 'application/json' },
//           body: JSON.stringify({ items: toAdd })
//         });
//       }

//       themeRefreshCart(false);
//     } finally {
//       pending = false;
//     }
//   }

//   var debounceTimer = null;
//   function scheduleReconcile(delay) {
//     clearTimeout(debounceTimer);
//     debounceTimer = setTimeout(reconcileMilestoneGifts, delay || 400);
//   }

//   document.documentElement.addEventListener('cart:refresh', function () { scheduleReconcile(400); });
//   document.addEventListener('cart:refresh', function () { scheduleReconcile(400); });
//   document.documentElement.addEventListener('cart:updated', function () { scheduleReconcile(400); });
//   document.addEventListener('cart:updated', function () { scheduleReconcile(400); });

//   if (window.jQuery) {
//     jQuery(document).ajaxComplete(function (event, xhr, settings) {
//       if (!settings || !settings.url) return;
//       if (/\/cart\/(add|change|update)(\.js)?/.test(settings.url)) {
//         scheduleReconcile(400);
//       }
//     });
//   }

//   // Initial load
//   reconcileMilestoneGifts();
// })();


(function () {
  var root = document.getElementById('mini-cart');
  if (!root) return;

  var pending = false;
  var latestCart = null;

  function getGiftMap() {
    var contentEl = root.querySelector('.mini-cart-drawer-content');
    if (!contentEl) return [];
    try {
      return JSON.parse(contentEl.getAttribute('data-product-gift-map') || '[]');
    } catch (e) {
      return [];
    }
  }

  function reconcile() {
    if (pending || !latestCart) return;
    var cart = latestCart;
    var giftMap = getGiftMap();
    var presentProductIds = cart.items.map(function (item) { return item.product_id; });
    var toAdd = [];
    var toRemove = [];

    giftMap.forEach(function (entry) {
      entry.giftVariantIds.forEach(function (giftVariantId) {
        var alreadyPresent = cart.items.some(function (item) {
          return item.variant_id === giftVariantId
            && item.properties
            && parseInt(item.properties._free_gift_of, 10) === entry.parentProductId;
        });
        if (!alreadyPresent) {
          toAdd.push({ id: giftVariantId, quantity: 1, properties: { _free_gift_of: entry.parentProductId } });
        }
      });
    });

    cart.items.forEach(function (item) {
      var props = item.properties || {};
      if (props._free_gift_of && presentProductIds.indexOf(parseInt(props._free_gift_of, 10)) === -1) {
        toRemove.push(item.key);
      }
    });

    if (!toAdd.length && !toRemove.length) return;

    pending = true;
    performReconciliation(toAdd, toRemove).finally(function () {
      pending = false;
      themeRefreshCart(false);
    });
  }

  async function performReconciliation(toAdd, toRemove) {
    for (var i = 0; i < toRemove.length; i++) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: toRemove[i], quantity: 0 })
      });
    }
    if (toAdd.length) {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: toAdd })
      });
    }
  }

  document.documentElement.addEventListener('cart:updated', function (event) {
    latestCart = event.detail.cart;
    reconcile();
  });

  // The gift-map data lives inside the section HTML, which swaps in
  // asynchronously after cart:refresh — re-check once that swap actually
  // happens, rather than guessing a delay.
  new MutationObserver(reconcile).observe(root, { childList: true });

  fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
    latestCart = cart;
    reconcile();
  });
})();

(function () {
  var reachedCheckpoints = {};
  var isInitialCheck = true;

  function fireCheckpointConfetti() {
    var canvas = document.getElementById('cart-confetti');

    if (!canvas || typeof confetti === 'undefined') return;

    var myConfetti = confetti.create(canvas, {
      resize: true,
      useWorker: true
    });

    var duration = 1500;
    var end = Date.now() + duration;

    (function frame() {
      myConfetti({
        particleCount: 12,
        spread: 80,
        angle: 90,
        origin: {
          x: Math.random(),
          y: 0
        },
        colors: [
          '#ff1744',
          '#d500f9',
          '#2979ff',
          '#00c853',
          '#ff9100',
          '#f50057'
        ],
        shapes: ['square'],
        scalar: 1.2,
        gravity: 0.8,
        drift: 0.5
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    })();
  }

  function checkMilestoneCheckpoints() {
    document
      .querySelectorAll('.shipiing-progress-track .check-point')
      .forEach(function (point) {

        var index = point.getAttribute('data-checkpoint-index');

        if (!index) return;

        var key = 'checkpoint-' + index;
        var isReached = point.classList.contains('has_magenta');

        /*
         * Initial page load:
         * Just remember which checkpoints are already reached.
         * DO NOT fire confetti.
         */
        if (isInitialCheck) {
          reachedCheckpoints[key] = isReached;
          return;
        }

        /*
         * Milestone crossed:
         * unchecked -> checked
         */
        if (isReached && !reachedCheckpoints[key]) {
          reachedCheckpoints[key] = true;
          fireCheckpointConfetti();
        }

        /*
         * Cart total dropped below milestone.
         * Reset so it can fire again when crossed.
         */
        if (!isReached && reachedCheckpoints[key]) {
          reachedCheckpoints[key] = false;
        }
      });

    isInitialCheck = false;
  }

  /*
   * Initial state only.
   * This records already-reached milestones without confetti.
   */
  checkMilestoneCheckpoints();

  /*
   * Run after cart refresh.
   * No MutationObserver.
   */
  document.addEventListener('cart:refresh', function () {
    setTimeout(function () {
      checkMilestoneCheckpoints();
    }, 0);
  });

})();


// function fireDrawerConfetti() {
//   var canvas = document.getElementById('cart-confetti');
//   console.log('confetttiiiii');
//   var myConfetti = confetti.create(canvas, {
//     resize: true,
//     useWorker: true
//   });

//   var duration = 2000;
//   var end = Date.now() + duration;

//   (function frame() {
//     console.log('innininin')
//     myConfetti({
//       particleCount: 12, // 🔥 doubled density
//       spread: 80,
//       angle: 90,
//       origin: { x: Math.random(), y: 0 },

//       // 🎨 Darker, vibrant colors
//       colors: [
//         '#ff1744', // red
//         '#d500f9', // purple
//         '#2979ff', // blue
//         '#00c853', // green
//         '#ff9100', // orange
//         '#f50057'  // pink
//       ],

//       // 🎀 Ribbon-like effect
//       shapes: ['square'], // better than default circles

//       scalar: 1.2, // slightly bigger pieces
//       gravity: 0.8,
//       drift: 0.5
//     });

//     if (Date.now() < end) {
//       requestAnimationFrame(frame);
//     }
//   })();
// }
// document.addEventListener('cart:refresh', function() {
//   fireDrawerConfetti();
// });
// =======================================================================

$(document).on('change', '.psf-pack-size input[type="radio"]', function () {
  var $price = $('psf-price');
  if (!$price.length) return;

  var packValue = $(this).val();

  // current flavour = the product this page is, so match the other option
  // against every variant and find the one whose pack option matches
  var variant = (window.__psfVariants || []).find(function (v) {
    return v.options.indexOf(packValue) !== -1;
  });
  if (!variant) return;

  renderPsfPrice($price, variant);
});

function renderPsfPrice($price, variant) {
  function psfMoney(cents) {
    return formatMoney(cents).replace(/[.,]00$/, '');
  }

  var servingMap = {};
  try { servingMap = JSON.parse($price.attr('data-serving-map') || '{}'); } catch (e) {}

  var rowHtml = '<span class="psf-price__current">' + psfMoney(variant.price) + '</span>';
  if (variant.compare_at_price > variant.price) {
    rowHtml += '<span class="psf-price__compare">' + psfMoney(variant.compare_at_price) + '</span>';
    rowHtml += '<span class="psf-price__save">Save ' + psfMoney(variant.compare_at_price - variant.price) + '</span>';
  }
  $price.find('[data-psf-price-row]').html(rowHtml);

  var serving = servingMap[variant.id];
  var $serving = $price.find('[data-psf-serving]');
  if (serving) {
    $serving.text(psfMoney(serving * 100) + ' / serving').removeAttr('hidden');
  } else {
    $serving.attr('hidden', 'hidden');
  }
}

(function () {
  var $sticky = $('[data-sticky-atc]');
  if (!$sticky.length) return;

  var mainWrapper = document.querySelector('.main-product-wrapper');
  var mainAtc = document.querySelector('.product-form__qty-atc, .product-form__buy-buttons');
  if (!mainWrapper || !mainAtc) return;

  var variants = [];
  try { variants = JSON.parse($sticky.attr('data-variants') || '[]'); } catch (e) {}

  // Visible while the main product wrapper is in view, hidden when it leaves
  var $wrapper = $(mainWrapper);

  function toggleStickyBar() {
    $sticky.toggleClass('is-active', isFullyOutOfView($wrapper)).removeAttr('hidden');
  }

  $(window).on('scroll resize', s6RafThrottle(toggleStickyBar));
  toggleStickyBar();

  // Sticky button clicks the real ATC so Focal handles loader + mini-cart
  $sticky.on('click', '[data-sticky-atc-button]', function () {
    var realBtn = mainAtc.querySelector('#AddToCart, [data-product-add-to-cart-button]');
    if (realBtn) realBtn.click();
  });

  // Live price on pack change
  function renderStickyPrice(variant) {
    if (!variant) return;
    var hasCompare = variant.compare > variant.price;

    $sticky.find('[data-sticky-pay]').text('Pay: ' + formatMoney(variant.price));
    $sticky.find('[data-sticky-mrp]')
      .text('MRP: ' + formatMoney(variant.compare))
      .attr('hidden', hasCompare ? null : 'hidden');
    $sticky.find('[data-sticky-save]')
      .text('(You SAVE ' + formatMoney(variant.compare - variant.price) + ')')
      .attr('hidden', hasCompare ? null : 'hidden');
  }

  // Pack radios drive the variant (flavour is fixed per product page)
  $(document).on('change', '.psf-pack-size input[type="radio"]', function () {
    var packValue = $(this).val();
    var variant = variants.find(function (v) {
      return v.options.indexOf(packValue) !== -1;
    });
    renderStickyPrice(variant);
  });

  document.addEventListener('variant:changed', function (event) {
    var v = event.detail && event.detail.variant;
    if (!v) return;
    renderStickyPrice({ price: v.price, compare: v.compare_at_price || 0 });
  });
})();

/* --- S6 URL coupon auto-apply (?discount_code=CODE) ----------------------- */
/* Lets a campaign link (WhatsApp / email / ads) pre-apply a discount code in
   OUR cart drawer, e.g. /products/supply6-mellow?discount_code=UNWIND25
   Why "discount_code" and not Shopify's "?discount=":
     - ReferRush intercepts ?discount= and throws "Unable to load your discount".
     - GoKwik's merchant.integration.js already reads ?discount_code= into its
       own 1-day cookie, so the same code is pre-filled at GoKwik checkout too.
   Behaviour:
     1. On load: remember the code (sessionStorage) and apply it with
        cart/update.js { discount: CODE }. This REPLACES whatever code is on
        the cart (e.g. GLAD15) - nothing on this store combines, so the old code
        would otherwise win and the new one would sit as "not applicable".
     2. Once the code is on the cart we are done, even while Shopify reports it
        "not applicable" (empty cart / product not added yet): Shopify parks the
        code and activates it by itself the moment a qualifying item is added.
        We only act again (after a cart:updated) if the code is NOT on the cart
        at all, and never after the shopper removes it themselves (x on the pill).
   Codes are passed to Shopify exactly as typed in the URL (no case changes). */
(function () {
  var KEY = 's6_url_coupon';
  var MAX_TRIES = 6;

  function readParam() {
    try {
      var p = new URLSearchParams(window.location.search);
      var v = p.get('discount_code') || p.get('code');
      if (!v) return null;
      v = String(v).trim();
      return /^[A-Za-z0-9_-]{2,40}$/.test(v) ? v : null;
    } catch (e) { return null; }
  }
  function getPending() {
    try { return JSON.parse(window.sessionStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
  }
  function setPending(obj) {
    try {
      if (obj) window.sessionStorage.setItem(KEY, JSON.stringify(obj));
      else window.sessionStorage.removeItem(KEY);
    } catch (e) {}
  }
  function cartHasCode(cart, code) {
    var list = (cart && cart.discount_codes) || [];
    for (var i = 0; i < list.length; i++) {
      if (s6SameCode(list[i].code, code)) return list[i];
    }
    return null;
  }

  var busy = false;
  function applyPending(refreshDrawer) {
    var pending = getPending();
    if (!pending || busy) return;
    if (pending.tries >= MAX_TRIES) { setPending(null); return; }
    busy = true;
    fetch(window.themeVariables.routes.cartUrl + '.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var hit = cartHasCode(cart, pending.code);
        var others = (cart && cart.discount_codes || []).filter(function (c) {
          return !s6SameCode(c.code, pending.code);
        });
        /* Done when our code is applied, or when it is parked alone on the cart
           (no competing code) - Shopify applies it once a qualifying item lands. */
        if (hit && (hit.applicable || others.length === 0)) { setPending(null); busy = false; return null; }
        pending.tries += 1;
        setPending(pending);
        return fetch(window.themeVariables.routes.cart_update_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: pending.code })
        }).then(function (r) { return r.json(); });
      })
      .then(function (updated) {
        busy = false;
        if (!updated) return;
        /* Shopify accepted the code (applied, or parked as the only code) - stop. */
        if (cartHasCode(updated, pending.code)) setPending(null);
        if (refreshDrawer && typeof window.themeRefreshCart === 'function') window.themeRefreshCart(false);
      })
      .catch(function () { busy = false; });
  }

  var fromUrl = readParam();
  if (fromUrl) setPending({ code: fromUrl, tries: 0 });

  if (getPending()) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { applyPending(true); });
    } else {
      applyPending(true);
    }
    /* Only fires while the code is still missing from the cart (pending is
       cleared as soon as Shopify holds it), so this cannot loop. */
    document.documentElement.addEventListener('cart:updated', function () {
      if (getPending()) setTimeout(function () { applyPending(true); }, 50);
    });
    /* Shopper removed a code on purpose - stop re-applying. */
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('[data-removecode]')) setPending(null);
    }, true);
  }
})();
